/**
 * CALL-CENTER-CRM-WORKSPACE-V1 -- the one-queue workspace page.
 *
 * ONE route, two role lenses decided SERVER-side (the /api/callcenter/
 * workspace read returns lens + items):
 *   - AGENT ("My Work"): the personal summary strip + ONE queue of the
 *     agent's own items, reason/due filters over it (ONE-QUEUE LAW: the
 *     tenant's buckets are FILTERS, never tabs).
 *   - MANAGER ("Team operations"): workload-by-agent (paused agents visible
 *     with their still-owned counts -- EXISTING-WORK PROTECTION), then the
 *     SAME one queue for the whole team with an agent drill filter that
 *     narrows IN PLACE (IN-PLACE DRILL LAW -- never a navigation away).
 *
 * FILTER STATE IS THE URL (the lead-list-url precedent): linkable,
 * refresh-safe, PHI-free (reason tokens + a due tier + a numeric staff id).
 *
 * ACTIONS (NO DEAD DOORS -- LES-016; SEE-NOT-DO rule 2):
 *   - Row "Open" -> the ONE LeadQuickViewHost (DRAWER LAW) -- rendered ONLY
 *     while me.leadsWorkspaceEnabled is true (the host's backing read;
 *     call_center_agent / owner hold it; gm_agm / regional_head do NOT, so
 *     they get no dead drawer). The audited phone reveal stays on the
 *     canonical full page; NO contact value renders here, ever.
 *   - Manager "Reassign" -> the EXISTING audited writer (POST
 *     /api/leads/:id/assign via assignLead + fetchAssignableStaff), rendered
 *     ONLY while me.leadAssignmentRoleCanAssign is true (server-declared
 *     authority, never client role logic).
 *   - NO Call dialer, NO WhatsApp send -- telephony and outbound messaging
 *     stay gated (charter binding law 3).
 *
 * ROUTING TRUTH (binding law 5): nothing here names or claims a rotation
 * strategy; least_loaded runs server-side and no strategy string renders.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './callcenter-workspace.css';
import { PeriodReports } from './daily-report/CallCenterDailyReportPage';
import { Link, useSearchParams } from 'react-router-dom';

import {
  assignLead,
  fetchAssignableStaff,
  fetchCallcenterWorkspace,
  releaseOvernightLeads,
  type AssignableStaffOption,
  type CallcenterWorkspaceItemView,
  type CallcenterWorkspaceView,
  type OvernightReleaseResultView,
  type WorkspaceWorkReason,
} from '@/dashboard/api';
import DashboardLoadError from '@/dashboard/DashboardLoadError';
import { useDashboardMeContext } from '@/dashboard/me-context';
import type { LeadQuickViewModel } from '@/dashboard/leads/lead-quickview-view';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import { humanizeDispositionSlug } from '@/dashboard/leads/last-contact-attempt';
import {
  AcBadge,
  AcButton,
  ConfirmDialog,
  DrawerShell,
  DataTable,
  Detail,
  WorkRegion,
  EmptyState,
  KpiTile,
  LoadingState,
  OpeningLine,
  PageHeader,
  SectionShell,
  carryVisualVariant,
  type DataTableColumn,
} from '@/dashboard/ui';
import { useVisualVariant } from '@/dashboard/ui/useVisualActivation';
import { useNarrowWorkspace } from '@/dashboard/callcenter/use-narrow-workspace';
import { formatIstDateTime } from '@/lib/datetime-ist';
import { formatClinicLocalDateTime } from '@/lib/age';
import {
  decideCallCenterWorkspaceAccess,
  managerEnquiryPreview,
  dueStateLabel,
  filterWorkspaceItems,
  managerBusinessStatus,
  leadOperationsDrillPath,
  composeManagerOpeningLine,
  presentWorkspaceLoadError,
  collapsedTileLabels,
  composeStripTileFacts,
  dueHasCountedTile,
  quietZeroLine,
  type StripTileState,
  morningPriorityExplainer,
  reasonHasCountedTile,
  ownerCellLabel,
  parseWorkspaceUrl,
  queueCountLine,
  serializeWorkspaceUrl,
  toggleReason,
  WORKSPACE_DUE_FILTER_LABELS,
  WORKSPACE_DUE_FILTERS,
  type CallCenterWorkspaceFilterState,
} from '@/dashboard/callcenter/callcenter-workspace-view';
import PhoneInEnquiryModal, {
  PHONE_IN_DOOR_LABEL,
  phoneInDoorVisible,
} from '@/dashboard/callcenter/PhoneInEnquiryModal';
import MyWorkV1 from '@/dashboard/callcenter/MyWorkV1';

// ---- Load state (the NoShowRecoveryPage idiom; test-injectable) ------------

export type WorkspaceLoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; view: CallcenterWorkspaceView }
  | { kind: 'error'; message: string; status?: number; code?: string };

export interface CallCenterWorkspacePageProps {
  /** TEST-ONLY state injection (react-dom/server renders cannot run
   *  effects); production callers render with no props and fetch live. */
  readonly initialState?: WorkspaceLoadState;
}

/** Honest row identity when the carved name is empty (never fabricated). */
export function rowDisplayName(item: CallcenterWorkspaceItemView): string {
  if (item.displayName.trim() !== '') return item.displayName;
  if (item.leadId !== null) return `Lead #${item.leadId}`;
  if (item.appointmentId !== null) return `Appointment #${item.appointmentId}`;
  return 'Name not recorded';
}

/** Chip tone per urgency (tone families, never color-alone -- the label
 *  always renders beside the tone). */
function urgencyTone(
  urgency: CallcenterWorkspaceItemView['urgency'],
): 'danger' | 'pending' | 'info' | 'neutral' {
  switch (urgency) {
    case 'breach':
      return 'danger';
    case 'due_now':
      return 'pending';
    case 'morning_priority':
      return 'info';
    default:
      return 'neutral';
  }
}

// ---- Manager reassign dialog (existing writer; existing authority) ---------

interface ReassignTarget {
  readonly leadId: number;
  readonly displayName: string;
  readonly currentOwnerLabel: string | null;
  readonly currentOwnerStaffId: number | null;
}

function ReassignDialog({
  target,
  onClose,
  onDone,
}: {
  readonly target: ReassignTarget;
  readonly onClose: () => void;
  readonly onDone: () => void;
}): JSX.Element {
  const [staff, setStaff] = useState<AssignableStaffOption[] | null>(null);
  const [selected, setSelected] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAssignableStaff()
      .then((options) => {
        if (!cancelled) setStaff(options);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the staff list. Close and retry.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const confirm = useCallback(() => {
    if (selected === '' || selected === target.currentOwnerStaffId || busy) return;
    setBusy(true);
    setError(null);
    assignLead(target.leadId, selected)
      .then(() => {
        setBusy(false);
        onDone();
      })
      .catch((e: unknown) => {
        setBusy(false);
        // ERROR PASSTHROUGH (LES-009): the server's 4xx message verbatim.
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [busy, onDone, selected, target.leadId, target.currentOwnerStaffId]);

  return (
    <ConfirmDialog
      eyebrow="Reassign lead"
      title={target.displayName}
      confirmLabel="Reassign"
      cancelLabel="Cancel"
      busy={busy}
      busyLabel="Reassigning..."
      confirmDisabled={selected === '' || selected === target.currentOwnerStaffId}
      onConfirm={confirm}
      onCancel={onClose}
      testId="workspace-reassign-dialog"
    >
      <p className="ac-text-body">
        Current owner:{' '}
        <strong>{target.currentOwnerLabel ?? 'Unassigned'}</strong>. The change
        is audited (who, when, previous and new owner).
      </p>
      {staff === null && error === null ? (
        <LoadingState label="Loading staff..." lines={2} />
      ) : null}
      {staff !== null ? (
        <select
          aria-label="New owner"
          data-testid="workspace-reassign-select"
          value={selected === '' ? '' : String(selected)}
          onChange={(e) =>
            setSelected(e.target.value === '' ? '' : Number(e.target.value))
          }
        >
          <option value="">Choose the new owner...</option>
          {staff.filter(s => s.staffUserId !== target.currentOwnerStaffId).map((s) => (
            <option key={s.staffUserId} value={String(s.staffUserId)}>
              {s.label}
            </option>
          ))}
        </select>
      ) : null}
      {error !== null ? (
        <p className="ac-text-caption" role="alert" data-testid="workspace-reassign-error">
          {error}
        </p>
      ) : null}
    </ConfirmDialog>
  );
}

/**
 * CALL-CENTER-TENANT-ROUTING-RULES-V1: the manager confirm for the overnight
 * release. Mirrors ReassignDialog (ConfirmDialog + a single POST + error
 * passthrough). The backend re-checks gate + hold + authority; this is a
 * confirm affordance only.
 */
function ReleaseOvernightDialog({
  count,
  onClose,
  onDone,
}: {
  readonly count: number;
  readonly onClose: () => void;
  readonly onDone: (result: OvernightReleaseResultView) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = useCallback(() => {
    if (busy) return;
    setBusy(true);
    setError(null);
    releaseOvernightLeads()
      .then((result) => {
        setBusy(false);
        onDone(result);
      })
      .catch((e: unknown) => {
        setBusy(false);
        // ERROR PASSTHROUGH (LES-009): the server's message verbatim.
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [busy, onDone]);

  return (
    <ConfirmDialog
      eyebrow="Release overnight leads"
      title={`${count} lead${count === 1 ? '' : 's'} awaiting release`}
      confirmLabel="Release"
      cancelLabel="Cancel"
      busy={busy}
      busyLabel="Releasing..."
      onConfirm={confirm}
      onCancel={onClose}
      testId="workspace-overnight-dialog"
    >
      <p className="ac-text-body">
        Each returning patient&apos;s lead goes back to the agent who handled
        them before; the rest are dealt evenly across the available agents. The
        change is audited (who, when, to whom).
      </p>
      {error !== null ? (
        <p
          className="ac-text-caption"
          role="alert"
          data-testid="workspace-overnight-error"
        >
          {error}
        </p>
      ) : null}
    </ConfirmDialog>
  );
}

// ---- Loading skeleton (mirrors the final layout -- no jump; 03 P11) --------

/** A layout-shaped loading state: a header line, the KPI band, and queue rows,
 *  so the page does not jump when the data arrives. Keeps the
 *  `workspace-loading` testId + an accessible status label. Lens-agnostic (the
 *  server picks the lens only once the data lands). */
export function WorkspaceLoadingSkeleton({
  variant = 'current',
}: {
  /** ADORACARE-VISUAL-SYSTEM-V1: the reference layout's own skeleton (one
   *  bar + the split work plane) so the v1 page does not jump either. */
  readonly variant?: 'current' | 'v1';
} = {}): JSX.Element {
  if (variant === 'v1') {
    return (
      <section
        className="callcenter-workspace callcenter-workspace--v1 ac-page--workspace workspace-skeleton"
        data-testid="workspace-loading"
        aria-busy="true"
      >
        <div className="workspace-skeleton__header">
          <span className="workspace-skeleton__strip workspace-skeleton__strip--title" />
          <span className="workspace-skeleton__strip workspace-skeleton__strip--sub" />
        </div>
        <div className="workspace-skeleton__bar" aria-hidden="true">
          {Array.from({ length: 7 }, (_, i) => (
            <span className="workspace-skeleton__chip" key={i} />
          ))}
        </div>
        <div className="workspace-skeleton__plane" aria-hidden="true">
          <div className="workspace-skeleton__rows">
            <LoadingState
              label="Loading your queue..."
              lines={6}
              testId="workspace-loading-status"
            />
          </div>
          <div className="workspace-skeleton__aside">
            <span className="workspace-skeleton__strip workspace-skeleton__strip--title" />
            <span className="workspace-skeleton__strip" />
            <span className="workspace-skeleton__strip workspace-skeleton__strip--label" />
          </div>
        </div>
      </section>
    );
  }
  return (
    <section
      className="callcenter-workspace workspace-skeleton"
      data-testid="workspace-loading"
      aria-busy="true"
    >
      <div className="workspace-skeleton__header">
        <span className="workspace-skeleton__strip workspace-skeleton__strip--title" />
        <span className="workspace-skeleton__strip workspace-skeleton__strip--sub" />
      </div>
      <div className="workspace-skeleton__band" aria-hidden="true">
        {Array.from({ length: 6 }, (_, i) => (
          <div className="workspace-skeleton__tile" key={i}>
            <span className="workspace-skeleton__strip workspace-skeleton__strip--num" />
            <span className="workspace-skeleton__strip workspace-skeleton__strip--label" />
          </div>
        ))}
      </div>
      {/* The filter bar is ALWAYS present in the ready state -- stub it so the
          queue does not shift down when the data lands (no layout jump). */}
      <div className="workspace-skeleton__filters" aria-hidden="true">
        {Array.from({ length: 7 }, (_, i) => (
          <span className="workspace-skeleton__chip" key={i} />
        ))}
      </div>
      <div className="workspace-skeleton__rows">
        <LoadingState
          label="Loading your queue..."
          lines={5}
          testId="workspace-loading-status"
        />
      </div>
    </section>
  );
}

// ---- The page --------------------------------------------------------------

export default function CallCenterWorkspacePage({
  initialState,
}: CallCenterWorkspacePageProps = {}): JSX.Element | null {
  const meCtx = useDashboardMeContext();
  const me = meCtx?.me ?? null;
  const monthlyManager = me?.role === 'call_center_manager';
  const [searchParams, setSearchParams] = useSearchParams();
  const [workSort,setWorkSort]=useState<'agent'|'open'|'overdue'>('overdue');
  const [workDescending,setWorkDescending]=useState(true);
  const queueSearch = searchParams.get('queueSearch') ?? '';
  const queueSource = searchParams.get('queueSource') ?? '';
  const queueState = searchParams.get('queueState') ?? '';
  const setQueueScope = (key: 'queueSource' | 'queueState', value: string) => {
    if (panelBusy) return;
    const next = new URLSearchParams(searchParams);
    next.delete('queueLead'); next.delete('queuePage');
    if (value) next.set(key, value); else next.delete(key);
    setOpenQuickView(null); setQueuePage(1); setSearchParams(next, { replace: true });
  };
  const setQueueSearch = (value: string) => {
    if (panelBusy) return;
    setOpenQuickView(null);
    setQueuePage(1);
    const next = new URLSearchParams(searchParams);
    next.delete('queueLead');
    next.delete('queuePage');
    if (value) next.set('queueSearch', value); else next.delete('queueSearch');
    setSearchParams(next, { replace: true });
  };
  const [queuePage,setQueuePage]=useState(()=>{const value=Number(searchParams.get('queuePage'));return Number.isInteger(value)&&value>0?value:1});
  const loadMoreRef=useRef<HTMLDivElement>(null);
  const [enquiryTab,setEnquiryTab]=useState<'overview'|'activity'>('overview');
  useEffect(()=>{setQueuePage(1)},[queueSearch,queueSource,queueState,searchParams.get('agent'),searchParams.get('agentId'),searchParams.get('due'),searchParams.get('reason')]);
  useEffect(()=>{
    const sentinel=loadMoreRef.current;
    if(!monthlyManager||!sentinel||typeof IntersectionObserver==='undefined')return;
    const observer=new IntersectionObserver(entries=>{
      if(entries.some(entry=>entry.isIntersecting)&&sentinel.dataset.more==='true'&&sentinel.dataset.busy!=='true')setQueuePage(page=>page+1);
    });
    observer.observe(sentinel);return()=>observer.disconnect();
  });
  const savedFocus=useRef<CallCenterWorkspaceFilterState|null>(null);
  const teamReturnAgent=useRef<number|null>(null);

  // ADORACARE-VISUAL-SYSTEM-V1: the reference visual system, DEFAULT OFF.
  // Resolved from the URL alone (`?visual=v1` on this route; the pure
  // ui/visual-variant.ts) and applied ONLY to the agent lens below -- the
  // manager lens and every URL without the switch render exactly as before.
  // ONE RESOLVED STATE (controller ruling on PR #773): the shell resolves the
  // variant once -- URL intent, tab memory and the route fence together --
  // applies it to the document and publishes it. This page READS that, and no
  // longer resolves its own from the URL alone. The old split showed itself
  // exactly here: activate, navigate away, come back on a bare URL, and the
  // document said data-visual="v1" while this page rendered the CURRENT
  // layout. The filter codec below keeps `carryVisualVariant`, which works
  // from `searchParams`, so this page no longer reads the location at all.
  const visualVariant = useVisualVariant();
  // TABLET DENSITY (controller item 3). Measured inside the REAL shell: at 834
  // the queue's rows ran 145-167px against 124px at 1440, so four rows filled
  // most of a tablet screen. See use-narrow-workspace.ts for what is dropped
  // (an information column, never a control) and why.
  const narrowWorkspace = useNarrowWorkspace();
  const [state, setState] = useState<WorkspaceLoadState>(
    initialState ?? { kind: 'loading' },
  );
  const [reloadKey, setReloadKey] = useState(0);
  const pageRefreshRef=useRef<HTMLButtonElement>(null);
  const restorePageRefresh=useRef(false);
  function refreshPage(): void {restorePageRefresh.current=true;setReloadKey(k=>k+1);}
  useEffect(()=>{if(state.kind!=='loading'&&restorePageRefresh.current){pageRefreshRef.current?.focus({preventScroll:true});restorePageRefresh.current=false;}},[state.kind]);
  // MY-WORK-RECOMPOSE-V4 set the CHROME plane's `data-visual` attribute from
  // THIS page's own effect. FACTORY-V6-ACTIVATION-V1 moved that act to the
  // shell (frontend/src/dashboard/ui/useVisualActivation.ts, mounted once in
  // DashboardLayout), because a plane that belongs to the DOCUMENT cannot be
  // owned by one page: set here, it died on the first nav link, could not
  // survive a refresh anywhere else, and never reached a print route.
  //
  // WHAT CHANGED ON THIS SCREEN, DECLARED: the shell's activation is fenced
  // by ROUTE, not by lens, so with the switch on, the manager lens on this
  // route now gets the V6 CHROME too. The manager lens's own PAGE markup is
  // untouched and still renders byte-identically with and without the switch
  // (pinned in __tests__/my-work-v1.test.tsx) -- `visualVariant` below still
  // decides that, exactly as before.
  // PRE-SELECTION, NOT AUTO-OPEN (CALL-CENTER-DRAWER-OCCLUSION-P0, founder
  // ruling 2026-09-02). The 2026-07-24 walk requirement -- PRIMARY WORK BEGINS
  // IN CONTEXT -- is HONOURED here, not reversed. Its first implementation
  // (CALL-CENTER-QUICKVIEW-DEFAULT-V1, Refs #315; ordered as item 3 of the
  // 2026-07-23 audit's founder direction, line 1013) delivered "in context" as
  // a DEFAULT-OPEN DRAWER, and that DEFEATED THE REQUIREMENT IT SERVED: the
  // drawer is a modal (scrim `position:fixed; inset:0; z-index:40` + a 430px
  // fixed slide-over), so on load it covered the whole NEXT ACTION column,
  // clipped LAST ACTIVITY mid-word, and put the entire queue behind a scrim
  // while showing a lead the operator had never chosen.
  //
  // The context an agent needs on load is KNOWING WHICH ROW IS THEIRS TO WORK
  // -- not a panel over the work. So the top row is PRE-SELECTED (visibly the
  // current row, per the banked `lead-list__row--selected` grammar) and the
  // drawer opens ONLY on the operator's intent (row click or Enter), showing
  // THAT row.
  //
  // DO NOT "RESTORE" THE AUTO-OPEN BELIEVING IT OBEYS THE WALK -- it does the
  // opposite. Regression pin: tests/e2e/role-acceptance/
  // callcenter-drawer-occlusion.spec.ts.
  //
  // `selectedKey` is the operator's own pick; null means "follow the queue's
  // top row" so a refetch/refilter re-derives it. `openQuickView` is the
  // drawer, and it is NEVER set by load.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [openQuickView, setOpenQuickView] = useState<{
    leadId: number;
    title: string;
  } | null>(null);
  const [panelBusy, setPanelBusy] = useState(false);
  const panelCloseRef = useRef<HTMLButtonElement>(null);
  const leadOpenerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (openQuickView !== null) panelCloseRef.current?.focus({ preventScroll: true });
  }, [openQuickView?.leadId, narrowWorkspace]);
  const [managerPreview, setManagerPreview] = useState<{
    leadId: number; model: LeadQuickViewModel | null; loading: boolean;
  } | null>(null);
  // Refresh fetches queue and preview independently; focus only after both
  // are mounted, regardless of which request finishes first.
  useEffect(()=>{if(state.kind==='ready'&&managerPreview?.model)panelCloseRef.current?.focus({preventScroll:true})},[managerPreview?.model,state.kind]);
  const requestedLead = searchParams.get('queueLead');
  useEffect(() => {
    if (!monthlyManager) return;
    setManagerPreview(null);setEnquiryTab('overview');
    if (!requestedLead || !/^[1-9]\d*$/.test(requestedLead) || !Number.isSafeInteger(Number(requestedLead))) {
      setOpenQuickView(null);
      return;
    }
    const leadId = Number(requestedLead);
    let cancelled = false;
    setOpenQuickView({ leadId, title: 'Enquiry' });
    setSelectedKey(`lead-${leadId}`);
    setManagerPreview({ leadId, model: null, loading: true });
    // A click or reload rechecks the existing workspace gate and exact row.
    // The current queue snapshot and an arbitrary URL are not read authority.
    fetchCallcenterWorkspace().then(view => {
      if (!cancelled) setManagerPreview({ leadId, model: managerEnquiryPreview(view, leadId), loading: false });
    }).catch(() => {
      if (!cancelled) setManagerPreview({ leadId, model: null, loading: false });
    });
    return () => { cancelled = true; };
  }, [monthlyManager, requestedLead, reloadKey]);
  const [reassign, setReassign] = useState<ReassignTarget | null>(null);
  // CALL-CENTER-TENANT-ROUTING-RULES-V1: the overnight release dialog + the
  // last release summary (rendered as a post-action banner on Team operations).
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseResult, setReleaseResult] =
    useState<OvernightReleaseResultView | null>(null);
  // PHONE-IN-LEAD-CAPTURE-V1: the agent's "log a phone enquiry" door
  // (agent lens only -- see phoneInDoorVisible).
  const [phoneInOpen, setPhoneInOpen] = useState(false);

  const access = decideCallCenterWorkspaceAccess({
    loading: meCtx?.loading ?? true,
    error: meCtx?.error ?? null,
    me,
  });

  useEffect(() => {
    if (initialState !== undefined) return;
    if (access.kind !== 'render') return;
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchCallcenterWorkspace()
      .then((view) => {
        if (!cancelled) setState({ kind: 'ready', view });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        // The two STRUCTURAL signals the presentation layer decides on --
        // read the same duck-typed way the status already was, so nothing in
        // the shared `portal-api` module (its owner's, not Lane 5's) changes.
        // `status` absent = the request failed or its body was not readable as
        // the API envelope; `code` is the backend's stable machine code.
        const status =
          typeof e === 'object' && e !== null && 'status' in e
            ? Number((e as { status: unknown }).status)
            : undefined;
        const rawCode =
          typeof e === 'object' && e !== null && 'code' in e
            ? (e as { code: unknown }).code
            : null;
        const code = typeof rawCode === 'string' && rawCode !== '' ? rawCode : undefined;
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : String(e),
          ...(status !== undefined && Number.isFinite(status) ? { status } : {}),
          ...(code !== undefined ? { code } : {}),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [access.kind, initialState, reloadKey]);

  const filters = useMemo(
    () => parseWorkspaceUrl(searchParams),
    [searchParams],
  );

  const writeFilters = useCallback(
    (next: CallCenterWorkspaceFilterState, clearSearch = false) => {
      if (!monthlyManager) {
        setSearchParams(carryVisualVariant(searchParams, serializeWorkspaceUrl(next)), { replace: true });
        return;
      }
      if (panelBusy) return;
      if (panelCloseRef.current) setOpenQuickView(null);
      // The filter codec serialises fresh params; the visual switch (when
      // present) is carried across so a filter click never flips the
      // founder's side-by-side back to the current render mid-walk.
      setSearchParams(
        (() => {const result=carryVisualVariant(searchParams, serializeWorkspaceUrl(next));for(const key of ['periodStart','periodEnd','source','agentId','view','queueSearch','queueSource','queueState','queueStatus']){const value=searchParams.get(key);if(value && !(clearSearch && ['queueSearch','queueSource','queueState','queueStatus'].includes(key)))result.set(key,value)}return result;})(),
        { replace: true },
      );
      setQueuePage(1);
    },
    [searchParams, setSearchParams, panelBusy, monthlyManager],
  );

  // The guard redirects before this page mounts in the app; this in-page
  // decision covers direct renders (and keeps tests honest).
  if (access.kind !== 'render') return null;

  const managerTitle=searchParams.get('view')==='agents'?'My team':searchParams.get('view')==='leads'?'Leads':'Team operations';
  const pageRefresh=<AcButton ref={pageRefreshRef} className="cc-range-trigger" data-testid="workspace-refresh-page" disabled={state.kind==='loading'} onClick={refreshPage}>Refresh</AcButton>;
  if(monthlyManager&&state.kind!=='ready'){
    const recovery=state.kind==='error'?presentWorkspaceLoadError(state.message,state.status,state.code):null;
    const denied=state.kind==='error'&&state.status===403&&state.code!=='NO_PROFILE';
    return <section className="callcenter-workspace cc-monthly-workspace" data-testid="workspace-manager" aria-label="Team operations">
      {false}
      <PeriodReports key="manager-report" summary heading={<PageHeader eyebrow="Call center manager" title={managerTitle}/>} workspaceRefresh={pageRefresh}/>
      {denied?<div className="cc-manager-state-card" role="alert" data-testid="workspace-error"><EmptyState title="Access restricted" body="Ask your administrator for access."/></div>:
       state.kind==='error'&&!recovery?.canRetry?<div data-testid="workspace-error"><DashboardLoadError message={recovery!.message} status={state.status} code={state.code}/>{recovery?.recoveryHint&&<p data-testid="workspace-error-recovery-hint">{recovery.recoveryHint}</p>}</div>:
       <div className="cc-manager-workplane">
        <SectionShell title="Team leads" testId="workspace-queue-section">
          <div className="cc-queue-toolbar"><label className="cc-queue-search"><span className="cc-sr-only">Search queue</span><input aria-label="Search queue" disabled placeholder="Search lead ID, name or agent" value={queueSearch}/></label>
            <label className="cc-queue-select"><select className="ac-select" aria-label="Queue source" disabled><option>All sources</option></select></label><label className="cc-queue-select"><select className="ac-select" aria-label="Queue agent" disabled><option>All agents</option></select></label><label className="cc-queue-select"><select className="ac-select" aria-label="Queue state" disabled><option>All states</option></select></label></div>
          <div className="ac-table-scroll cc-filtered-empty-table" aria-busy={state.kind==='loading'} data-testid={state.kind==='loading'?'workspace-loading':'workspace-error'}>
            <table className="table ac-table" aria-label="Team queue"><colgroup>{[25,17,16,16,26].map((width,index)=><col key={index} style={{width:`${width}%`}}/>)}</colgroup><thead><tr>{['Lead','Source','Status','Owner','Next action'].map(label=><th key={label}>{label}</th>)}</tr></thead><tbody><tr><td colSpan={5}>
              <div role="status"><EmptyState title={state.kind==='loading'?'Loading leads…':'Could not load leads'}
                body={state.kind==='error'?'Report totals load separately.':undefined}
                action={state.kind==='error'?<AcButton data-testid="workspace-error-retry" onClick={refreshPage}>Retry</AcButton>:undefined}/></div>
            </td></tr></tbody></table>
          </div>
        </SectionShell>
        <aside className="cc-workload-rail"><SectionShell title="Needs attention" testId="workspace-workload"><p className="cc-state-rail">{state.kind==='loading'?'Loading workload…':'Workload unavailable.'}</p></SectionShell></aside>
       </div>}
    </section>;
  }

  if (state.kind === 'loading') {
    return <WorkspaceLoadingSkeleton variant={visualVariant} />;
  }
  if (state.kind === 'error') {
    // ERROR-RECOVERY-V2 (Lane 5, 2026-09-10). THE RECOVERY MATCHES THE ERROR.
    //
    // V1 printed "This is usually temporary" and a Try again button under
    // EVERY failure. A 401, a 403, an unprovisioned account and a 404 on a
    // section still rolling out are none of them temporary, and retrying does
    // nothing for any of them -- so V1 sat a useless button and a false claim
    // directly under a card that had just given the real remedy.
    //
    // `presentWorkspaceLoadError` (callcenter-workspace-view.ts) mirrors
    // DashboardLoadError's own branch conditions and decides three things:
    // whether the card already handles this case (then this page adds NOTHING),
    // what message the card should be handed, and whether a retry is honest.
    //
    // DashboardLoadError IS NOT TOUCHED. It is shared by the workboard, the
    // doctor surfaces and the patient record, and it belongs to its own owner;
    // Lane 5 mirrors it from the outside instead of editing it. The mirror is
    // pinned by frontend/src/dashboard/callcenter/__tests__/workspace-error-recovery.test.ts.
    //
    // THE MESSAGE IS ALWAYS CONTROLLED COPY in the card's verbatim branch,
    // because that branch renders whatever it is handed EXACTLY AS GIVEN. The
    // first version of this asked whether the message LOOKED technical, which
    // failed open on every exception containing spaces -- "DatabaseError:
    // staff_lead_load_failed", "permission denied for table leads", a JSON
    // parse error quoting an HTML page. There is no shape test here now: the
    // caller's text is never inspected and never rendered. Wording is
    // lens-neutral -- the error renders before the view arrives, so an agent
    // and a manager read the same words.
    const recovery = presentWorkspaceLoadError(state.message, state.status, state.code);
    return (
      <section className="callcenter-workspace" data-testid="workspace-error">
        {/* THE CODE IS FORWARDED, not just the status (controller item 2).
            DashboardLoadError classifies NO_PROFILE from `code` FIRST, before
            its 401 sign-in branch, precisely so an authenticated-but-
            unprovisioned user is not told to sign in again. This page was
            reading the code (the classifier consults it) and then NOT handing
            it to the card -- so a NO_PROFILE 401 whose MESSAGE happened not to
            contain the words "no staff profile" fell through to the sign-in
            card and told an already-authenticated operator to sign in. The
            card's own message-text fallback covered the common phrasing and
            hid the gap. */}
        <DashboardLoadError
          message={recovery.message}
          {...(state.status !== undefined ? { status: state.status } : {})}
          {...(state.code !== undefined ? { code: state.code } : {})}
        />
        {recovery.recoveryHint !== null ? (
          <p className="ac-text-caption" data-testid="workspace-error-recovery-hint">
            {recovery.recoveryHint}
          </p>
        ) : null}
        {recovery.canRetry ? (
          <button
            type="button"
            className="ac-btn ac-btn--primary"
            data-testid="workspace-error-retry"
            onClick={() => {
              setState({ kind: 'loading' });
              setReloadKey((k) => k + 1);
            }}
          >
            Try again
          </button>
        ) : null}
      </section>
    );
  }

  const { view } = state;
  const isManager = view.lens === 'manager';
  const agentLabelById = new Map<number, string>(
    [
      ...(view.manager?.workload ?? []).map((w): [number, string] => [w.staffUserId, w.label]),
      ...(monthlyManager ? view.items.flatMap((item): [number, string][] => item.owner.staffId === null ? [] : [[item.owner.staffId, item.owner.label ?? `Agent #${item.owner.staffId}`]]) : []),
    ],
  );
  const sourceKey = (item: CallcenterWorkspaceItemView) => item.sourceKind === 'source' ? `source:${item.source}` : item.sourceKind ?? 'unavailable';
  const sourceOptions = Array.from(new Map(view.items.filter(item => item.sourceKind).map(item => [sourceKey(item), item.sourceKind === 'source' ? item.source! : item.sourceKind === 'unknown' ? 'Unknown source' : 'Unlinked appointments'])), ([value,label]) => ({value,label})).sort((a,b)=>a.label.localeCompare(b.label));
  const stateOptions = [...new Set(view.items.map(item=>item.state ?? ''))].sort().map(value=>({value:value ? `state:${value}` : 'unknown',label:value || 'State unavailable'}));
  const scopedQueueItems = view.items.filter(item => (!queueSource || sourceKey(item) === queueSource) && (!queueState || (item.state ? `state:${item.state}` : 'unknown') === queueState));
  const normalizedQueueSearch = queueSearch.trim().toLocaleLowerCase();
  const businessStatus=searchParams.get('queueStatus')??'';
  const statusItems=filterWorkspaceItems(scopedQueueItems,filters,agentLabelById,true);
  const businessStatuses=[...new Set(statusItems.map(managerBusinessStatus))];
  function setBusinessStatus(value: string): void {const next=new URLSearchParams(searchParams);value?next.set('queueStatus',value):next.delete('queueStatus');next.delete('queueLead');setSearchParams(next);setQueuePage(1);}
  const visibleItems = filterWorkspaceItems(monthlyManager ? scopedQueueItems : view.items, filters, agentLabelById, monthlyManager).filter(item => !monthlyManager || ((!businessStatus || managerBusinessStatus(item)===businessStatus) && (!normalizedQueueSearch || [item.displayName, item.safeLeadId, item.owner.label ?? (item.owner.staffId === null ? null : agentLabelById.get(item.owner.staffId))].some(value => value?.toLocaleLowerCase().includes(normalizedQueueSearch)))));
  const totalQueuePages=Math.max(1,Math.ceil(visibleItems.length/12));
  const currentQueuePage=Math.min(queuePage,totalQueuePages);
  const pagedItems=isManager?visibleItems.slice(0,currentQueuePage*12):visibleItems;
  const myTeam=isManager && searchParams.get('view')==='agents';
  const workload=[...(view.manager?.workload??[])].map(row=>monthlyManager&&!myTeam?{...row,openCount:view.items.filter(item=>item.owner.staffId===row.staffUserId&&item.leadId!==null).length,overdueCount:view.items.filter(item=>item.owner.staffId===row.staffUserId&&item.leadId!==null&&item.urgency==='breach').length}:row).sort((a,b)=>{
    const order=workSort==='agent'?a.label.localeCompare(b.label):workSort==='open'?a.openCount-b.openCount:a.overdueCount-b.overdueCount;
    return (workDescending?-order:order)||a.staffUserId-b.staffUserId;
  });

  const quickViewLawful = me?.leadsWorkspaceEnabled === true;
  const canReassign = isManager && me?.leadAssignmentRoleCanAssign === true;

  // ONE row identity, used by the table, the pre-selection and the open door
  // alike -- so "the selected row" and "the row the drawer shows" can never
  // drift apart.
  const rowKeyOf = (item: CallcenterWorkspaceItemView): string =>
    item.leadId !== null
      ? `lead-${item.leadId}`
      : item.safeLeadId !== null
        ? `safe-${item.safeLeadId}`
        : `appt-${item.appointmentId}`;

  // PRE-SELECTION target: the TOP lawful lead in the current (possibly
  // filtered) queue. AGENT LENS ONLY -- the manager "Team operations" lens is
  // a triage surface, not a per-lead work surface (the lens rule banked by
  // CALL-CENTER-QUICKVIEW-DEFAULT-V1 s.3 is preserved) -- and only while the
  // caller holds the drawer's backing read (quickViewLawful; never a door that
  // opens nothing, per SEE-NOT-DO / LES-016). Rows with no leadId
  // (appointment-only work) are skipped: the drawer needs a lead.
  //
  // This marks a row. It opens NOTHING.
  const preSelectedItem =
    !isManager && quickViewLawful
      ? visibleItems.find((it) => it.leadId !== null) ?? null
      : null;
  // The operator's own pick wins; otherwise follow the queue's top row, so a
  // refetch or a filter change re-derives the current row instead of stranding
  // the marker on a row that is no longer there.
  const currentRowKey =
    selectedKey !== null &&
    visibleItems.some((it) => rowKeyOf(it) === selectedKey)
      ? selectedKey
      : preSelectedItem !== null
        ? rowKeyOf(preSelectedItem)
        : null;

  // The operator's intent door: mark the row AND open its drawer. EXACTLY the
  // audience the existing "Open lead" button already had (quickViewLawful + a
  // leadId), so this adds an affordance, never an authority.
  //
  // NO DEAD DOOR (SEE-NOT-DO / LES-016), and the marker keeps ONE meaning --
  // "the row you are working". A row that cannot be opened is not a door: it
  // is not clickable, not focusable, and clicking it moves nothing. That is
  // why the whole-row door is withheld from a caller without the backing read
  // (below) and why this is a strict no-op for an appointment-only row, which
  // pre-selection skips for the same reason: the drawer needs a lead.
  const canOpenRow = (item: CallcenterWorkspaceItemView): boolean =>
    (quickViewLawful || monthlyManager) && item.leadId !== null;
  const openRow = (item: CallcenterWorkspaceItemView, opener?: HTMLElement): void => {
    if (!canOpenRow(item) || (isManager && panelBusy)) return;
    const rowButton = document.querySelector<HTMLButtonElement>(`[data-testid="workspace-${monthlyManager?'name':'open'}-${item.leadId}"]`);
    const active = document.activeElement;
    // Use the selected row's explicit door for both row and button activation,
    // never a previously focused filter or a transient table-cell target.
    leadOpenerRef.current = opener ?? rowButton ?? (active instanceof HTMLElement ? active : null);
    setSelectedKey(rowKeyOf(item));
    setOpenQuickView({
      leadId: item.leadId as number,
      title: rowDisplayName(item),
    });
    if(monthlyManager){const next=new URLSearchParams(searchParams);next.set('queueLead',String(item.leadId));next.set('queuePage',String(currentQueuePage));setSearchParams(next,{replace:true});}
  };

  // QUEUE-COLUMN-WIDTHS (Lane 5, 2026-09-10, controller item 7). MEASURED, not
  // guessed: on the RUNNING page at 834 the OWNER cell wrapped agent names
  // across two lines ("Agent / A") and the DUE cell compressed, because with no
  // declared widths DataTable gives every column an identical share.
  //
  // The mechanism is the SIBLING TABLE'S OWN: COLUMN-WIDTHS-V1 -- the optional
  // DataTableColumn.width prop and the <colgroup> it renders -- added to the
  // shared DataTable by lane 3 (founder authorisation 2026-09-09, condition A)
  // and already used by the COC follow-up register. This CONSUMES that prop; it
  // does not modify DataTable, and it adds no CSS, so no shared stylesheet is
  // touched. Widths are proportional so they hold at both designed widths.
  const legacyColumns: DataTableColumn<CallcenterWorkspaceItemView>[] = ([
    {
      key: 'who',
      header: 'Person',
      width: '22%',
      render: (item) => (
        <div>
          <strong data-testid="workspace-row-name">{rowDisplayName(item)}</strong>
          {item.clinicLabel !== null ? (
            <div className="ac-text-caption">{item.clinicLabel}</div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'reason',
      width: '14%',
      header: 'Why now',
      render: (item) => (
        <div>
          <AcBadge
            tone={urgencyTone(item.urgency)}
            testId={`workspace-reason-${item.workReason}`}
          >
            {item.workReasonLabel}
          </AcBadge>
          {/* THE REASON IS ON THE GLASS, not in a tooltip. It used to render
              the words "Morning priority" with the explanation hidden in a
              title= attribute -- invisible when scanning, and unreachable
              entirely on a touch screen, which is how this desk is used. The
              explainer is the same org-config sentence, now readable. */}
          {item.morningPriority ? (
            <div
              className="ac-text-caption"
              data-testid="workspace-morning-priority"
            >
              {morningPriorityExplainer(view)}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'due',
      width: '16%',
      header: 'Due',
      render: (item) => (
        // The urgency ramp applied WHERE THE EYE SCANS (the Due cell) --
        // presentation of the item's existing urgency token only.
        <span data-testid="workspace-row-due" data-due-urgency={item.urgency}>
          <span className="workspace-cell-label">Due</span>
          {dueStateLabel(item)}
        </span>
      ),
    },
    {
      key: 'owner',
      width: '16%',
      header: 'Owner',
      render: (item) => (
        <span data-testid="workspace-row-owner">
          <span className="workspace-cell-label">Owner</span>
          {ownerCellLabel(item.owner)}
          {item.owner.kind === 'exception' && item.exceptionReason !== null ? (
            // Humanized presentation of the server's reason token (the raw
            // no_eligible_agents leak, audit S5) -- the SAME slug humanizer
            // the record already uses; value semantics untouched.
            <span className="ac-text-caption">
              {' '}
              ({humanizeDispositionSlug(item.exceptionReason) ?? item.exceptionReason})
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'activity',
      width: '14%',
      header: 'Last activity',
      // Named so the occlusion regression can hit-test THIS cell by handle.
      // It is the column that read as CLEAN to a DOM scan while the image
      // showed it severed mid-word (CALL-CENTER-DRAWER-OCCLUSION-P0).
      render: (item) =>
        item.lastActivity === null ? (
          <span className="ac-text-caption" data-testid="workspace-row-activity">
            <span className="workspace-cell-label">Last activity</span>
            No activity recorded
          </span>
        ) : (
          <span data-testid="workspace-row-activity">
            <span className="workspace-cell-label">Last activity</span>
            {item.lastActivity.label}
            {item.lastActivity.atIso !== null
              ? ` - ${formatIstDateTime(item.lastActivity.atIso) + (monthlyManager ? ' IST' : '')}`
              : ''}
          </span>
        ),
    },
    {
      key: 'next',
      width: '18%',
      header: 'Next action',
      render: (item) => {
        // ONE primary door per row (03 P7): "Open lead" is the primary; the
        // manager "Reassign" is the demoted secondary. Both render ONLY from
        // the server-declared authority (SEE-NOT-DO -- never a dead door).
        const hasOpen = canOpenRow(item);
        const hasReassign = canReassign && item.leadId !== null;
        return (
          <div className="workspace-row-actions">
            <span
              className="workspace-row-actions__next"
              data-testid="workspace-row-next"
            >
              {item.nextActionLabel}
            </span>
            {monthlyManager && <div className="cc-manager-secondary">{legacyCell('due', item)}</div>}
            {hasOpen || hasReassign ? (
              // The whole row is now the open door, so the in-row buttons must
              // swallow the event -- otherwise "Reassign" would ALSO open the
              // drawer behind its dialog.
              <div
                className="workspace-row-actions__buttons"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                {hasOpen ? (
                  <AcButton
                    variant="primary"
                    size="sm"
                    disabled={isManager && panelBusy}
                    data-testid={`workspace-open-${item.leadId}`}
                    onClick={() => openRow(item)}
                  >
                    Open lead
                  </AcButton>
                ) : null}
                {hasReassign ? (
                  <AcButton
                    variant="ghost"
                    size="sm"
                    disabled={panelBusy}
                    data-testid={`workspace-reassign-${item.leadId}`}
                    onClick={() =>
                      setReassign({
                        leadId: item.leadId as number,
                        displayName: rowDisplayName(item),
                        currentOwnerLabel: item.owner.label,
                        currentOwnerStaffId: monthlyManager ? item.owner.staffId : null,
                      })
                    }
                  >
                    Reassign
                  </AcButton>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      },
    },
  ] as DataTableColumn<CallcenterWorkspaceItemView>[]).filter((c) =>
    // TABLET DENSITY: at narrow widths the LAST ACTIVITY column is dropped and
    // its 14% is redistributed below. It is the lowest-value cell on a triage
    // row -- an informational note, no action, no count, no owner state -- and
    // dropping it is what brings the row back to one screenful. Every control
    // stays at every width.
    narrowWorkspace && !monthlyManager ? c.key !== 'activity' : true,
  ).map((c) =>
    monthlyManager
      ? { ...c, width: ({who:'17%',reason:'15%',due:'17%',owner:'14%',activity:'17%',next:'20%'} as Record<string,string>)[c.key] }
      : narrowWorkspace && (c.key === 'who' || c.key === 'due')
      ? { ...c, width: c.key === 'who' ? '28%' : '20%' }
      : c,
  );

  // Manager reference: five columns. Retain every urgency/activity fact as
  // secondary content rather than hiding it or changing the agent's table.
  const legacyCell = (key: string, item: CallcenterWorkspaceItemView) =>
    legacyColumns.find(column => column.key === key)?.render(item);
  const columns: DataTableColumn<CallcenterWorkspaceItemView>[] = monthlyManager ? [
    {key:'who',header:'Lead',width:'25%',render:item=><div className="cc-manager-lead">
      <AcButton data-testid={`workspace-name-${item.leadId}`} className="cc-quiet cc-lead-name" variant="ghost" disabled={!canOpenRow(item)||panelBusy} onKeyDown={event=>event.stopPropagation()} onClick={event=>{event.stopPropagation();openRow(item,event.currentTarget);}}><strong data-testid="workspace-row-name">{rowDisplayName(item)}</strong></AcButton>
      <small>#{item.safeLeadId??item.leadId??'—'}</small>
    </div>},
    {key:'source',header:'Source',width:'17%',render:item=><span data-testid="workspace-row-source">{item.sourceKind==='source'&&item.source?item.source:item.sourceKind==='unlinked'?'No linked enquiry':'Unknown source'}</span>},
    {key:'state',header:'Status',width:'16%',render:item=><div className="cc-manager-state"><AcBadge tone={managerBusinessStatus(item)==='New lead'?'info':managerBusinessStatus(item)==='Contacted'?'positive':managerBusinessStatus(item)==='Callback'?'pending':managerBusinessStatus(item)==='Needs review'?'danger':'neutral'} testId="workspace-row-state">{managerBusinessStatus(item)}</AcBadge>
    </div>},
    {key:'owner',header:'Owner',width:'16%',render:item=>legacyCell('owner',item)},
    {key:'next',header:'Next action',width:'26%',render:item=><div className="cc-manager-next"><span data-testid="workspace-row-next">{item.nextActionLabel}</span>

    </div>},
  ] : legacyColumns;

  function selectWorkloadAgent(id: number, overdue=false): void {
    if(myTeam)teamReturnAgent.current=id;
    if(!savedFocus.current)savedFocus.current={...filters};
    const next=new URLSearchParams(searchParams);next.set('view','leads');next.set('agent',String(id));next.delete('reason');next.delete('queueStatus');next.delete('queueSource');next.delete('queueState');next.delete('queueSearch');overdue?next.set('due','breach'):next.delete('due');next.delete('queueLead');next.delete('queuePage');setSearchParams(next);setQueuePage(1);
  }
  function allAgents(): void {
    if(teamReturnAgent.current!==null){
      const next=new URLSearchParams(searchParams);next.set('view','agents');
      for(const key of ['agent','due','reason','queueLead','queueStatus','queueSource','queueState','queueSearch'])next.delete(key);
      setSearchParams(next);savedFocus.current=null;setQueuePage(1);return;
    }
    writeFilters(savedFocus.current??{...filters,agentStaffId:null});savedFocus.current=null;setQueuePage(1);
  }


  const renderWorkloadAction = (row: (typeof workload)[number]) => <span className="workspace-row-drills"><AcButton size="sm" data-testid={`workspace-drill-${row.staffUserId}`} aria-label={filters.agentStaffId===row.staffUserId?'Show whole team':'View agent work'} aria-pressed={filters.agentStaffId===row.staffUserId} onClick={()=>{
              if(filters.agentStaffId===row.staffUserId){writeFilters(savedFocus.current??{...filters,agentStaffId:null});savedFocus.current=null;}
              else {if(!savedFocus.current)savedFocus.current={...filters};writeFilters({...filters,agentStaffId:row.staffUserId});}
              setQueuePage(1);
            }}>{filters.agentStaffId===row.staffUserId?'All agents':'View agent work'}</AcButton>{quickViewLawful&&<Link data-testid={`workspace-leadops-${row.staffUserId}`} aria-label={`Open in Lead operations: ${row.label}`} to={leadOperationsDrillPath(row.staffUserId)}>Lead operations</Link>}</span>;

  const recoveryUnavailable = view.unavailableSources?.some(
    source => source.source === 'no_show_recovery' && source.reason === 'permission_denied',
  ) === true;
  const exceptionsLabel = recoveryUnavailable
    ? 'Unassigned exceptions (permitted work)'
    : 'Unassigned exceptions (all work types)';
  const strip = view.strip;
  // Composed once per render from the SAME view the tiles below draw from,
  // so the sentence and the numbers can never disagree.
  const openingLine = composeManagerOpeningLine(view);
  // COMMAND-CENTER-CRAFT-V2: the because-lines the tiles were missing.
  const tileFacts = composeStripTileFacts(view);

  // The strip, DECLARED ONCE as data, before any of it is rendered. Each
  // tile's active test used to be written out two or three times per tile;
  // naming it here is why the collapse decision can be a pure function over
  // the same triples the tiles are drawn from, instead of a list accumulated
  // as a side effect while JSX children evaluate.
  //
  // LABELS ARE VERBATIM the tile labels (ONE-NUMBER LAW): the quiet line
  // inherits these strings, so a shortened copy here would silently drop the
  // scope a corrective put on a count.
  const newLeadActive =
    filters.reasons.length === 1 && filters.reasons[0] === 'new_lead';
  const atRiskActive =
    filters.reasons.length === 1 &&
    filters.reasons[0] === 'appointment_confirmation';
  const exceptionsActive =
    filters.reasons.length === 1 && filters.reasons[0] === 'routing_exception';
  const stripTiles: readonly StripTileState[] = [
    { label: 'New leads', value: strip.newLeads, active: newLeadActive },
    { label: 'Due now', value: strip.dueNow, active: filters.due === 'due_now' },
    { label: 'Overdue (SLA breaches)', value: strip.overdue, active: filters.due === 'breach' },
    { label: 'Appointments at risk', value: strip.appointmentsAtRisk, active: atRiskActive },
    { label: 'Morning priority', value: strip.morningPriority, active: filters.due === 'morning_priority' },
    ...(isManager
      ? [{
          label: exceptionsLabel,
          value: strip.exceptions,
          active: exceptionsActive,
        }]
      : []),
  ];
  const collapsedTiles = collapsedTileLabels(stripTiles);
  const drawsTile = (label: string): boolean => !collapsedTiles.includes(label);

  // ADORACARE-VISUAL-SYSTEM-V1 -- THE REFERENCE SCREEN, agent lens only,
  // behind the switch. Same view, same filters, same row identity, same
  // audience predicates (canOpenRow) and the same phone-in door; the row
  // MARKS (the panel follows) and never opens the modal drawer. Everything
  // below this branch is the current render, byte-for-byte.
  if (!isManager && visualVariant === 'v1') {
    return (
      <>
        <MyWorkV1
          view={view}
          filters={filters}
          writeFilters={writeFilters}
          visibleItems={visibleItems}
          rowKeyOf={rowKeyOf}
          currentRowKey={currentRowKey}
          selectRow={(item) => {
            if (!canOpenRow(item)) return;
            setSelectedKey(rowKeyOf(item));
          }}
          canSelectRow={canOpenRow}
          quickViewLawful={quickViewLawful}
          leadHrefOf={(item) =>
            // The SAME audience the detail's "Open full view" door reads
            // (LeadQuickView: me.leadsWorkspaceEnabled) -- never a dead link.
            quickViewLawful && item.safeLeadId !== null && item.safeLeadId.trim() !== ''
              ? `/dashboard/leads/${item.safeLeadId}`
              : null
          }
          onRefresh={() => setReloadKey((k) => k + 1)}
          onLeadChanged={() => {
            // The lead has been worked in the panel: refetch, and let the
            // re-sorted queue's top row become the current row again.
            setSelectedKey(null);
            setReloadKey((k) => k + 1);
          }}
          rowDisplayName={rowDisplayName}
          phoneInDoor={
            phoneInDoorVisible(view.lens) ? (
              // The page-level door stays reachable, DEMOTED to secondary:
              // the ONE primary action on this screen lives where the work
              // is (the selected lead's panel), not in the chrome.
              <AcButton
                variant="secondary"
                onClick={() => setPhoneInOpen(true)}
                data-testid="workspace-phone-in-door"
              >
                {PHONE_IN_DOOR_LABEL}
              </AcButton>
            ) : undefined
          }
        />
        {phoneInOpen ? (
          <PhoneInEnquiryModal
            visual="v1"
            onClose={(captured) => {
              setPhoneInOpen(false);
              if (captured) setReloadKey((k) => k + 1);
            }}
          />
        ) : null}
      </>
    );
  }

  if (!monthlyManager) {
  return (
    <section
      className="callcenter-workspace"
      data-testid={isManager ? 'workspace-manager' : 'workspace-agent'}
      aria-label={isManager ? 'Team operations' : 'My Work'}
    >
      <PageHeader
        eyebrow={monthlyManager ? "Call center manager" : "Call center"}
        title={isManager ? 'Team operations' : 'My Work'}
        /* PHONE-IN-LEAD-CAPTURE-V1: the ONE page-level primary action
           (shell law), agent lens only. The lens is SERVER-decided
           (call_center_agent exactly), and that role is in the FROZEN
           write audience of POST /api/leads/enquiries -- the door never
           renders for a caller the write route would refuse
           (SEE-NOT-DO / LES-016). */
        actions={
          phoneInDoorVisible(view.lens) ? (
            <AcButton
              variant="primary"
              onClick={() => setPhoneInOpen(true)}
              data-testid="workspace-phone-in-door"
            >
              {PHONE_IN_DOOR_LABEL}
            </AcButton>
          ) : undefined
        }
        subs={monthlyManager ? [] : [
          <span className="workspace-freshness" key="freshness">
            {`As of ${formatClinicLocalDateTime(
              view.generatedAtIso,
              view.generatedAtZone,
            )} (${view.generatedAtZone})`}
            <button
              type="button"
              className="ac-btn ac-btn--ghost ac-btn--sm workspace-freshness__refresh"
              onClick={() => setReloadKey((k) => k + 1)}
              data-testid="workspace-refresh"
            >
              Refresh
            </button>
          </span>,
        ]}
        testId="workspace-header"
      />

      {/* COMMAND-CENTER-CRAFT-V1: the S1 OPENING LINE, manager lens only.
          The agent lens is a personal worklist -- "My Work" already names one
          job, and a synthesis sentence over your own six rows would be noise.
          The MANAGER lens is the one that asks a person to synthesize seven
          tiles and a workload table into "where is it piling up, who needs
          help", so the sentence goes here and only here.
          Every clause is composed from figures ALREADY on this page (the
          strip + the workload rows) -- no new fetch, no derivation beyond
          counting what is rendered. */}
      {/* EMPTY-OPENING-LINE-V1 (Lane 5, 2026-09-10). The clause count is part
          of the condition, and that is not a nicety -- it is this module's own
          stated law being honoured at the render site. composeManagerOpeningLine
          documents "NO CLAUSE, NO LINE ... it does NOT fall back to restating a
          tile" and returns { state:'quiet', clauses: [] } to say so. The page
          ignored that and mounted the primitive anyway, and OpeningLine always
          emits its <section> plus the LED dot -- so a manager opening a quiet
          board met an EMPTY ALERT BANNER: a coloured box with a bullet and no
          words. Reproduced in this package's empty-state frames at 1440 and
          834 before this change.
          The fix is here rather than in the primitive on purpose:
          frontend/src/dashboard/ui/** is a registry SHARED SURFACE owned
          elsewhere, and a primitive that renders what it is given is not the
          thing that is wrong -- the caller asking for an empty one is. */}
      {isManager && !monthlyManager && openingLine.clauses.length > 0 ? (
        <OpeningLine
          state={openingLine.state}
          clauses={openingLine.clauses}
          testId="workspace-opening-line"
        />
      ) : null}

      {recoveryUnavailable ? (
        <EmptyState
          title="Missed-appointment information unavailable"
          body="Your role does not have access to missed-appointment recovery. Queue counts and filters below cover permitted work only; they exclude missed appointments."
          testId="workspace-recovery-unavailable"
        />
      ) : null}

      {view.cappedSources.length > 0 ? (
        <p className="panel-caveat" data-testid="workspace-capped">
          Large queue: some feeds are showing their newest items only
          ({view.cappedSources.join(', ')}), so counts are lower bounds.
        </p>
      ) : null}

      {/* KPI BAND (charter s.1): a single compact scope-labelled band sits
          directly under the title for BOTH lenses -- the manager's per-agent
          workload detail follows it. Each count is a filter affordance into
          its own view; the scope stays IN the label (the corrective's truth,
          restyled not reworded). */}
      {/* SCOPE LABELS V1 (Lane 5, 2026-09-10; controller item 3b). "Overdue"
          appears TWICE on the manager screen and the two figures MEASURE
          DIFFERENT THINGS, traced to source rather than assumed:

            THE TILE      computeWorkspaceStrip (api/routes/callcenter-workspace.ts)
                          -- rows composed into THIS QUEUE VIEW whose urgency is
                          'breach', excluding routing exceptions. Clock = each
                          item's own slaAnchorIso.
            THE WORKLOAD  getStaffLeadLoad (api/routes/lead-assignment.ts)
                          -- EVERY non-terminal lead in the ORGANISATION assigned
                          to that agent, aged from leads.created_at against the
                          assignment-escalation setting.

          Different population, different clock, different threshold: neither is
          a subset of the other, so they are NOT expected to reconcile. No
          calculation is changed to make them agree -- the screen says which is
          which, which is what was actually missing. Manager lens only: the
          agent lens shows one of these figures and needs no disambiguation. */}
      {isManager && !monthlyManager ? (
        <p className="ac-text-caption" data-testid="workspace-strip-scope">
          {recoveryUnavailable
            ? 'Counts for permitted queue work only; missed appointments are excluded.'
            : 'Counts for this queue.'} Each agent&apos;s own total is below.
        </p>
      ) : null}

      <div className="workspace-strip" data-testid="workspace-strip">
        {/* COMMAND-CENTER-CRAFT-V2: a tile at zero does not draw a card. Its
            label is collected into `collapsedTiles` above and named in one
            quiet line below the strip, so the FACT survives and the real
            estate does not. `drawsTile` reads the decision the pure helper
            already made -- the JSX never makes it. */}
        {drawsTile('New leads') ? (
          <KpiTile
            label="New leads"
            value={strip.newLeads}
            tone={strip.newLeads > 0 ? 'warm' : 'ok'}
            active={newLeadActive}
            onClick={() =>
              writeFilters({
                ...filters,
                reasons: newLeadActive ? [] : ['new_lead'],
              })
            }
            sub={tileFacts.newLeads}
            testId="workspace-tile-new"
          />
        ) : null}
        {drawsTile('Due now') ? (
          <KpiTile
            label="Due now"
            value={strip.dueNow}
            tone={strip.dueNow > 0 ? 'warm' : 'ok'}
            active={filters.due === 'due_now'}
            onClick={() =>
              writeFilters({
                ...filters,
                due: filters.due === 'due_now' ? 'all' : 'due_now',
              })
            }
            sub={tileFacts.dueNow}
            testId="workspace-tile-due"
          />
        ) : null}
        {drawsTile('Overdue (SLA breaches)') ? (
          <KpiTile
            label="Overdue (SLA breaches)"
            value={strip.overdue}
            tone={strip.overdue > 0 ? 'breach' : 'ok'}
            active={filters.due === 'breach'}
            onClick={() =>
              writeFilters({
                ...filters,
                due: filters.due === 'breach' ? 'all' : 'breach',
              })
            }
            sub={tileFacts.overdue}
            testId="workspace-tile-overdue"
          />
        ) : null}
        {drawsTile('Appointments at risk') ? (
          <KpiTile
            label="Appointments at risk"
            value={strip.appointmentsAtRisk}
            tone={strip.appointmentsAtRisk > 0 ? 'breach' : 'ok'}
            active={atRiskActive}
            onClick={() =>
              writeFilters({
                ...filters,
                reasons: atRiskActive ? [] : ['appointment_confirmation'],
              })
            }
            sub={tileFacts.appointmentsAtRisk}
            testId="workspace-tile-atrisk"
          />
        ) : null}
        {drawsTile('Morning priority') ? (
          <KpiTile
            label="Morning priority"
            value={strip.morningPriority}
            tone="ok"
            hint={morningPriorityExplainer(view)}
            active={filters.due === 'morning_priority'}
            onClick={() =>
              writeFilters({
                ...filters,
                due:
                  filters.due === 'morning_priority' ? 'all' : 'morning_priority',
              })
            }
            sub={tileFacts.morningPriority}
            testId="workspace-tile-morning"
          />
        ) : null}
        {isManager && drawsTile(exceptionsLabel) ? (
          <KpiTile
            label={exceptionsLabel}
            value={strip.exceptions}
            tone={strip.exceptions > 0 ? 'breach' : 'ok'}
            active={exceptionsActive}
            onClick={() =>
              writeFilters({
                ...filters,
                reasons: exceptionsActive ? [] : ['routing_exception'],
              })
            }
            sub={tileFacts.exceptions}
            testId="workspace-tile-exceptions"
          />
        ) : null}
        {/* OVERNIGHT HOLD (CALL-CENTER-TENANT-ROUTING-RULES-V1): a manager-only
            count tile, present ONLY when the overnight-hold seam is active
            (view.manager.overnight defined). A pure count -- no filter click,
            and NOT part of the zero-collapse: its whole job is to say whether
            a hold is outstanding, so "0" is the answer a manager came for. */}
        {isManager && view.manager?.overnight !== undefined ? (
          <KpiTile
            label={`Overnight awaiting release (${view.manager.overnight.scopeLabel})`}
            value={
              view.manager.overnight.capped
                ? `${view.manager.overnight.awaitingRelease}+`
                : view.manager.overnight.awaitingRelease
            }
            tone={view.manager.overnight.awaitingRelease > 0 ? 'warm' : 'ok'}
            hint="Leads that arrived outside working hours are held unassigned until a manager releases them."
            testId="workspace-tile-overnight"
          />
        ) : null}
      </div>

      {/* COMMAND-CENTER-CRAFT-V2: one quiet sentence for everything at zero,
          instead of a full card each. The fact survives; the real estate does
          not. A tile that is the ACTIVE filter keeps its card even at zero,
          so the control you are standing on never vanishes underneath you. */}
      {quietZeroLine(collapsedTiles) !== null ? (
        <p className="workspace-strip-clear" data-testid="workspace-strip-clear">
          {recoveryUnavailable ? 'Permitted queue work: ' : ''}
          {quietZeroLine(collapsedTiles)}
        </p>
      ) : null}

      {isManager && view.manager !== undefined ? (
        <SectionShell
          eyebrow="Team"
          title="Workload by agent"
          sub="Each agent's whole assigned load, not just this queue. A paused agent keeps their work."
          testId="workspace-workload"
        >
          {view.manager.workload === null ? (
            <EmptyState
              title="Workload view unavailable"
              body="Your configured assignment authority does not include the per-agent load read."
              testId="workspace-workload-unavailable"
            />
          ) : (
            <DataTable
              label="Workload by agent"
              testId="workspace-workload-table"
              columns={[
                // COLUMN-WIDTHS-V1 ON THE WORKLOAD TABLE (Lane 5, 2026-09-10;
                // controller item 4). The 834 frame showed "Agent A" and
                // "Agent B" broken across two lines HERE, because the earlier
                // round applied the shared DataTable width prop to the QUEUE
                // table only and left this one on the equal-share default,
                // where the drill cell's two controls take the room the name
                // needs.
                //
                // THE VALUES ARE SIZED FOR THIS TABLE, not copied from the
                // queue: five columns of quite different content -- a person's
                // name, a badge that can read "Paused - keeps existing work",
                // two right-aligned figures under long scope-carrying headers,
                // and a cell holding a button plus a link. Measured at 834 on
                // the running page (the walk prints the agent cell's height
                // against its line-height and fails if the name wraps).
                {
                  key: 'agent',
                  width: '18%',
                  header: 'Agent',
                  render: (row) => <strong>{row.label}</strong>,
                },
                {
                  key: 'status',
                  width: '17%',
                  header: 'Status',
                  render: (row) =>
                    row.paused ? (
                      <AcBadge tone="pending" testId={`workspace-paused-${row.staffUserId}`}>
                        Paused - keeps existing work
                      </AcBadge>
                    ) : (
                      <AcBadge tone="positive">Active</AcBadge>
                    ),
                },
                {
                  key: 'open',
                  width: '15%',
                  header: 'Open (agent total)',
                  align: 'end',
                  render: (row) => (
                    <span>
                      <span className="workspace-cell-label">Open (agent total)</span>
                      {row.openCount}
                    </span>
                  ),
                },
                {
                  key: 'overdue',
                  width: '17%',
                  // SCOPE LABELS V1: the bare word "Overdue" here sat a few
                  // hundred pixels under a tile also labelled "Overdue" while
                  // counting a different population on a different clock.
                  header: 'Overdue (agent total)',
                  align: 'end',
                  render: (row) => (
                    <span>
                      <span className="workspace-cell-label">Overdue (agent total)</span>
                      {row.overdueCount}
                    </span>
                  ),
                },
                {
                  key: 'drill',
                  width: '33%',
                  header: '',
                  render: (row) => (
                    <span className="workspace-row-drills">
                      <AcButton
                        variant="secondary"
                        size="sm"
                        aria-pressed={filters.agentStaffId === row.staffUserId}
                        data-testid={`workspace-drill-${row.staffUserId}`}
                        onClick={() =>
                          writeFilters({
                            ...filters,
                            agentStaffId:
                              filters.agentStaffId === row.staffUserId
                                ? null
                                : row.staffUserId,
                          })
                        }
                      >
                        {filters.agentStaffId === row.staffUserId
                          ? 'Show whole team'
                          : 'Focus this agent'}
                      </AcButton>
                      {/* MANAGER DRILL-DOWN LAW (FIX 4): a real LINK into the ONE
                          canonical Lead Operations table filtered to this agent
                          -- rendered ONLY where the caller can reach that read
                          (never a dead door; no read widened). The in-place
                          filter above stays the "My Work-shaped rows per agent"
                          drill (both are sanctioned by the surface model). */}
                      {quickViewLawful ? (
                        <Link
                          className="ac-inline-link"
                          to={leadOperationsDrillPath(row.staffUserId)}
                          data-testid={`workspace-leadops-${row.staffUserId}`}
                        >
                          Open in Lead operations
                        </Link>
                      ) : null}
                    </span>
                  ),
                },
              ]}
              rows={view.manager.workload}
              rowKey={(row) => row.staffUserId}
              empty={
                <EmptyState
                  title="No assigned work yet"
                  body="No agent currently owns open leads."
                />
              }
            />
          )}
          {view.manager.pausedWithNoOpenWork.length > 0 ? (
            <p className="ac-text-caption" data-testid="workspace-paused-noload">
              Paused with no open work:{' '}
              {view.manager.pausedWithNoOpenWork.map((p) => p.label).join(', ')}
            </p>
          ) : null}
          {/* MANAGER DRILL-DOWN LAW (FIX 4): the unassigned exceptions drill into
              the ONE canonical Lead Operations unassigned view -- a real LINK
              where the caller holds that read (never a dead door). */}
          {quickViewLawful ? (
            <p className="ac-text-caption" data-testid="workspace-drill-canonical">
              Open in Lead operations:{' '}
              <Link
                className="ac-inline-link"
                to={leadOperationsDrillPath('unassigned')}
                data-testid="workspace-leadops-unassigned"
              >
                Unassigned pool
              </Link>
            </p>
          ) : null}
        </SectionShell>
      ) : null}

      {/* OVERNIGHT HOLD (CALL-CENTER-TENANT-ROUTING-RULES-V1): the manager
          release action + the last-run summary. Rendered ONLY when the
          overnight-hold seam is active (the /me release signal + the count
          fragment both present) -- absent when the rule is off (FAIL-SAME). */}
      {isManager &&
      me?.overnightReleaseEnabled === true &&
      view.manager?.overnight !== undefined ? (
        <SectionShell
          eyebrow="Team"
          title="Overnight leads"
          sub="Leads captured outside working hours are held unassigned until you release them."
          testId="workspace-overnight"
        >
          <p className="ac-text-body" data-testid="workspace-overnight-count">
            <strong>{view.manager.overnight.awaitingRelease}</strong> overnight
            lead{view.manager.overnight.awaitingRelease === 1 ? '' : 's'} awaiting
            release ({view.manager.overnight.scopeLabel}).
          </p>
          <div className="workspace-overnight-actions">
            <AcButton
              variant="primary"
              size="sm"
              data-testid="workspace-overnight-release"
              disabled={view.manager.overnight.awaitingRelease === 0}
              onClick={() => setReleaseOpen(true)}
            >
              Release overnight leads
            </AcButton>
          </div>
          {releaseResult !== null ? (
            <div
              className="workspace-overnight-summary"
              role="status"
              data-testid="workspace-overnight-summary"
            >
              <p className="ac-text-body">
                Released <strong>{releaseResult.releasedCount}</strong> of{' '}
                {releaseResult.heldCount} ({releaseResult.continuityCount} to a
                prior agent, {releaseResult.dealtCount} dealt across the team
                {releaseResult.skippedCount > 0
                  ? `, ${releaseResult.skippedCount} skipped`
                  : ''}
                ).
              </p>
              {releaseResult.perAgent.length > 0 ? (
                <ul className="workspace-overnight-tally">
                  {releaseResult.perAgent.map((a) => (
                    <li key={a.staffUserId}>
                      {a.label}: {a.count}
                      {a.viaContinuity > 0
                        ? ` (${a.viaContinuity} returning)`
                        : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </SectionShell>
      ) : null}

      {/* QUEUE-HEADING-V1 (Lane 5, 2026-09-10): the queue now carries a
          VISIBLE name and an honest count, in the SAME SectionShell primitive
          the workload block above already uses. Before this, DataTable's
          `label` reached only `aria-label`, so the eye met an unnamed,
          uncounted table under a row of chips -- reproduced at 1440 and 834 in
          this package's frames. No new primitive is minted and no CSS file is
          touched (dashboard.css belongs to another lane). */}
      <SectionShell
        eyebrow={monthlyManager?undefined:"Queue"}
        title={monthlyManager ? filters.agentStaffId===null?'Team leads':`${agentLabelById.get(filters.agentStaffId)??'Selected agent'}’s leads` : isManager?'Team queue':'My queue'}
        sub={`${queueCountLine(visibleItems.length, view.items.length)}${recoveryUnavailable ? ' - permitted work only; missed appointments excluded' : ''}`}
        testId="workspace-queue-section"
      >
      <div
        className="workspace-filters"
        role="group"
        aria-label="Queue filters"
        data-testid="workspace-filters"
      >
        {/* KEPT DELIBERATELY (COMMAND-CENTER-CRAFT-V2). Dropping this label
            would have shaved one element off the census, and it is exactly
            the trade the founder's amendment forbids: "do not delete a
            control someone uses to do their job in order to win a count".
            ENTERPRISE-EXPERIENCE-V2 shipped it as the five-second scan
            anchor for this group and pinned it; aria-label serves screen
            readers, not the eye it was written for. The count stays higher. */}
        <span className="workspace-filters__label" aria-hidden="true">Filters</span>
        {/* COMMAND-CENTER-CRAFT-V1: chips whose filter already has a counted
            tile above are NOT rendered -- the tile is the same control with a
            count and a tone on it. Nothing becomes unreachable; the duplicate
            stops taking space. */}
        {view.availableReasons
          .filter((reason) => !reasonHasCountedTile(reason, isManager))
          .map((reason) => {
          // CORRECTIVE-747 ROUND 2 (Gap 1a): the label is authored for the
          // WHOLE set this chip counts, never sampled from one arbitrary
          // member via .find() -- a set containing 1 due callback and 3
          // promised (upcoming) callbacks must not wear the label of the
          // one member .find() happened to return ("Callback due 4" when
          // three of the four are not due). WORKSPACE_REASON_FALLBACK_LABELS
          // is now the ONE label source for every reason chip, not merely
          // its empty-set fallback.
          const label = WORKSPACE_REASON_FALLBACK_LABELS[reason];
          const active = filters.reasons.includes(reason);
          // COMMAND-CENTER-CRAFT-V2: the surviving chips were the only
          // controls on this screen with no count anywhere. They filter the
          // LOADED items client-side, so a count of those items is exactly
          // what the chip does -- not an estimate.
          const count = view.items.filter((i) => i.workReason === reason).length;
          return (
            <button
              key={reason}
              type="button"
              className={`ac-btn ac-btn--${active ? 'primary' : 'ghost'} ac-btn--sm`}
              aria-pressed={active}
              data-testid={`workspace-filter-${reason}`}
              onClick={() => writeFilters(toggleReason(filters, reason))}
            >
              {`${label} ${count}`}
            </button>
          );
        })}
        <span className="workspace-filters__divider" aria-hidden="true" />
        {WORKSPACE_DUE_FILTERS.filter((due) => !dueHasCountedTile(due)).map((due) => (
          <button
            key={due}
            type="button"
            className={`ac-btn ac-btn--${filters.due === due ? 'primary' : 'ghost'} ac-btn--sm`}
            aria-pressed={filters.due === due}
            data-testid={`workspace-due-${due}`}
            onClick={() => writeFilters({ ...filters, due })}
          >
            {WORKSPACE_DUE_FILTER_LABELS[due]}
          </button>
        ))}
        {filters.agentStaffId !== null ? (
          <AcBadge tone="info" testId="workspace-agent-filter">
            {`Agent: ${agentLabelById.get(filters.agentStaffId) ?? `Staff #${filters.agentStaffId}`}`}
          </AcBadge>
        ) : null}
      </div>


      <DataTable
        label={isManager ? 'Team queue' : 'My queue'}
        testId="workspace-queue"
        columns={columns}
        rows={visibleItems}
        rowKey={rowKeyOf}
        // The CURRENT ROW is marked, never covered. Grammar reused verbatim
        // from the banked `lead-list__row--selected` idiom (brand wash + a
        // 3px inset strip -- a strip AND a wash, never colour alone).
        rowClassName={(item) =>
          rowKeyOf(item) === currentRowKey
            ? 'callcenter-workspace__row--current'
            : undefined
        }
        rowTestId={(item) =>
          rowKeyOf(item) === currentRowKey ? 'workspace-row-current' : undefined
        }
        // The operator's intent door. DataTable makes the row focusable and
        // activates it on Enter, so this is the click AND the keyboard path.
        onRowClick={quickViewLawful ? openRow : undefined}
        empty={
          view.items.length === 0 && monthlyManager ? (
            <div className="ac-table-scroll cc-filtered-empty-table"><table className="table ac-table" aria-label="Team queue"><colgroup>{columns.map(column=><col key={column.key} style={{width:column.width}}/>)}</colgroup><thead><tr>{columns.map(column=><th key={column.key}>{column.header}</th>)}</tr></thead><tbody><tr><td colSpan={columns.length}><EmptyState title="No leads to show" testId="workspace-empty" body={`Only work your role can access is shown.${recoveryUnavailable?' Missed-appointment recovery is not included.':''}`}/></td></tr></tbody></table></div>
          ) : view.items.length === 0 ? (
            <EmptyState
              title={recoveryUnavailable ? 'No permitted queue work to show' : isManager ? 'The team is all caught up' : 'You are all caught up'}
              body={recoveryUnavailable
                ? 'Missed-appointment information is unavailable, so this is not a complete view of team work.'
                : 'Nothing needs work right now. New work appears here the moment it arrives.'}
              testId="workspace-empty"
            />
          ) : (
            <EmptyState
              title={monthlyManager?"No leads match these filters":"Nothing matches these filters"}
              body={monthlyManager?"Try a different lead ID, source or agent.":"Clear a filter to see the rest of the queue."}
              action={
                <AcButton
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    writeFilters({ reasons: [], due: 'all', agentStaffId: null })
                  }
                >
                  Clear filters
                </AcButton>
              }
              testId="workspace-empty-filtered"
            />
          )
        }
      />
      </SectionShell>

      {openQuickView !== null ? (
        <LeadQuickViewHost
          leadId={openQuickView.leadId}
          seedTitle={openQuickView.title}
          onClose={() => {
            // Closing returns the operator to the readable queue. The row
            // stays marked, so they can see where they were.
            setOpenQuickView(null);
          }}
          onChanged={() => {
            // The lead has been worked: close back to the queue and refetch,
            // so the re-sorted queue's top row becomes the current row again
            // (continuous one-queue work -- without a modal on load).
            setOpenQuickView(null);
            setSelectedKey(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {reassign !== null ? (
        <ReassignDialog
          target={reassign}
          onClose={() => setReassign(null)}
          onDone={() => {
            setReassign(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {releaseOpen && view.manager?.overnight !== undefined ? (
        <ReleaseOvernightDialog
          count={view.manager.overnight.awaitingRelease}
          onClose={() => setReleaseOpen(false)}
          onDone={(result) => {
            setReleaseResult(result);
            setReleaseOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {/* PHONE-IN-LEAD-CAPTURE-V1: the capture modal. On a close after at
          least one capture the queue refetches, so a phone-in that routed
          to this agent appears without a manual refresh. The refresh runs
          on CLOSE (never mid-success) so the open modal is never unmounted
          under the agent by the loading skeleton. */}
      {phoneInOpen ? (
        <PhoneInEnquiryModal
          onClose={(captured) => {
            setPhoneInOpen(false);
            if (captured) setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </section>
  );
  }

  const queueCounters = (<div className={isManager ? "cc-queue-counters" : undefined}>
      {/* COMMAND-CENTER-CRAFT-V1: the S1 OPENING LINE, manager lens only.
          The agent lens is a personal worklist -- "My Work" already names one
          job, and a synthesis sentence over your own six rows would be noise.
          The MANAGER lens is the one that asks a person to synthesize seven
          tiles and a workload table into "where is it piling up, who needs
          help", so the sentence goes here and only here.
          Every clause is composed from figures ALREADY on this page (the
          strip + the workload rows) -- no new fetch, no derivation beyond
          counting what is rendered. */}
      {/* EMPTY-OPENING-LINE-V1 (Lane 5, 2026-09-10). The clause count is part
          of the condition, and that is not a nicety -- it is this module's own
          stated law being honoured at the render site. composeManagerOpeningLine
          documents "NO CLAUSE, NO LINE ... it does NOT fall back to restating a
          tile" and returns { state:'quiet', clauses: [] } to say so. The page
          ignored that and mounted the primitive anyway, and OpeningLine always
          emits its <section> plus the LED dot -- so a manager opening a quiet
          board met an EMPTY ALERT BANNER: a coloured box with a bullet and no
          words. Reproduced in this package's empty-state frames at 1440 and
          834 before this change.
          The fix is here rather than in the primitive on purpose:
          frontend/src/dashboard/ui/** is a registry SHARED SURFACE owned
          elsewhere, and a primitive that renders what it is given is not the
          thing that is wrong -- the caller asking for an empty one is. */}


      {view.cappedSources.length > 0 ? (
        <p className="panel-caveat" data-testid="workspace-capped">
          Large queue: some feeds are showing their newest items only
          ({view.cappedSources.join(', ')}), so counts are lower bounds.
        </p>
      ) : null}

      {/* KPI BAND (charter s.1): a single compact scope-labelled band sits
          directly under the title for BOTH lenses -- the manager's per-agent
          workload detail follows it. Each count is a filter affordance into
          its own view; the scope stays IN the label (the corrective's truth,
          restyled not reworded). */}
      {/* SCOPE LABELS V1 (Lane 5, 2026-09-10; controller item 3b). "Overdue"
          appears TWICE on the manager screen and the two figures MEASURE
          DIFFERENT THINGS, traced to source rather than assumed:

            THE TILE      computeWorkspaceStrip (api/routes/callcenter-workspace.ts)
                          -- rows composed into THIS QUEUE VIEW whose urgency is
                          'breach', excluding routing exceptions. Clock = each
                          item's own slaAnchorIso.
            THE WORKLOAD  getStaffLeadLoad (api/routes/lead-assignment.ts)
                          -- EVERY non-terminal lead in the ORGANISATION assigned
                          to that agent, aged from leads.created_at against the
                          assignment-escalation setting.

          Different population, different clock, different threshold: neither is
          a subset of the other, so they are NOT expected to reconcile. No
          calculation is changed to make them agree -- the screen says which is
          which, which is what was actually missing. Manager lens only: the
          agent lens shows one of these figures and needs no disambiguation. */}
      {isManager && !monthlyManager ? (
        <p className="ac-text-caption" data-testid="workspace-strip-scope">
          {!monthlyManager&&recoveryUnavailable?'Counts for permitted queue work only; missed appointments are excluded.':'Counts for this queue.'} Each agent&apos;s own total is in Workload by agent.
        </p>
      ) : null}

      <div className="workspace-strip" data-testid="workspace-strip">
        {/* COMMAND-CENTER-CRAFT-V2: a tile at zero does not draw a card. Its
            label is collected into `collapsedTiles` above and named in one
            quiet line below the strip, so the FACT survives and the real
            estate does not. `drawsTile` reads the decision the pure helper
            already made -- the JSX never makes it. */}
        {drawsTile('New leads') ? (
          <KpiTile
            label="New leads"
            value={strip.newLeads}
            tone={strip.newLeads > 0 ? 'warm' : 'ok'}
            active={newLeadActive}
            onClick={() =>
              writeFilters({
                ...filters,
                reasons: newLeadActive ? [] : ['new_lead'],
              })
            }
            sub={tileFacts.newLeads}
            testId="workspace-tile-new"
          />
        ) : null}
        {drawsTile('Due now') ? (
          <KpiTile
            label="Due now"
            value={strip.dueNow}
            tone={strip.dueNow > 0 ? 'warm' : 'ok'}
            active={filters.due === 'due_now'}
            onClick={() =>
              writeFilters({
                ...filters,
                due: filters.due === 'due_now' ? 'all' : 'due_now',
              })
            }
            sub={tileFacts.dueNow}
            testId="workspace-tile-due"
          />
        ) : null}
        {drawsTile('Overdue (SLA breaches)') ? (
          <KpiTile
            label="Overdue (SLA breaches)"
            value={strip.overdue}
            tone={strip.overdue > 0 ? 'breach' : 'ok'}
            active={filters.due === 'breach'}
            onClick={() =>
              writeFilters({
                ...filters,
                due: filters.due === 'breach' ? 'all' : 'breach',
              })
            }
            sub={tileFacts.overdue}
            testId="workspace-tile-overdue"
          />
        ) : null}
        {drawsTile('Appointments at risk') ? (
          <KpiTile
            label="Appointments at risk"
            value={strip.appointmentsAtRisk}
            tone={strip.appointmentsAtRisk > 0 ? 'breach' : 'ok'}
            active={atRiskActive}
            onClick={() =>
              writeFilters({
                ...filters,
                reasons: atRiskActive ? [] : ['appointment_confirmation'],
              })
            }
            sub={tileFacts.appointmentsAtRisk}
            testId="workspace-tile-atrisk"
          />
        ) : null}
        {drawsTile('Morning priority') ? (
          <KpiTile
            label="Morning priority"
            value={strip.morningPriority}
            tone="ok"
            hint={monthlyManager?undefined:morningPriorityExplainer(view)}
            active={filters.due === 'morning_priority'}
            onClick={() =>
              writeFilters({
                ...filters,
                due:
                  filters.due === 'morning_priority' ? 'all' : 'morning_priority',
              })
            }
            sub={tileFacts.morningPriority}
            testId="workspace-tile-morning"
          />
        ) : null}
        {isManager && drawsTile(exceptionsLabel) ? (
          <KpiTile
            label={exceptionsLabel}
            value={strip.exceptions}
            tone={strip.exceptions > 0 ? 'breach' : 'ok'}
            active={exceptionsActive}
            onClick={() =>
              writeFilters({
                ...filters,
                reasons: exceptionsActive ? [] : ['routing_exception'],
              })
            }
            sub={tileFacts.exceptions}
            testId="workspace-tile-exceptions"
          />
        ) : null}
        {/* OVERNIGHT HOLD (CALL-CENTER-TENANT-ROUTING-RULES-V1): a manager-only
            count tile, present ONLY when the overnight-hold seam is active
            (view.manager.overnight defined). A pure count -- no filter click,
            and NOT part of the zero-collapse: its whole job is to say whether
            a hold is outstanding, so "0" is the answer a manager came for. */}
        {isManager && view.manager?.overnight !== undefined ? (
          <KpiTile
            label={`Overnight awaiting release (${view.manager.overnight.scopeLabel})`}
            value={
              view.manager.overnight.capped
                ? `${view.manager.overnight.awaitingRelease}+`
                : view.manager.overnight.awaitingRelease
            }
            tone={view.manager.overnight.awaitingRelease > 0 ? 'warm' : 'ok'}
            hint="Leads that arrived outside working hours are held unassigned until a manager releases them."
            testId="workspace-tile-overnight"
          />
        ) : null}
      </div>

      {/* COMMAND-CENTER-CRAFT-V2: one quiet sentence for everything at zero,
          instead of a full card each. The fact survives; the real estate does
          not. A tile that is the ACTIVE filter keeps its card even at zero,
          so the control you are standing on never vanishes underneath you. */}
      {!isManager && quietZeroLine(collapsedTiles) !== null ? (
        <p className="workspace-strip-clear" data-testid="workspace-strip-clear">
          {quietZeroLine(collapsedTiles)}
        </p>
      ) : null}

  </div>);

  // Same authorized organization workload as before; stage breakdown and
  // complete staffing totals are not supplied by that read.
  const teamRows=[...(view.manager?.workload??[]),...(view.manager?.pausedWithNoOpenWork??[])
    .filter(agent=>!view.manager?.workload?.some(row=>row.staffUserId===agent.staffUserId))
    .map(agent=>({...agent,paused:true,openCount:0,overdueCount:0}))]
    .sort((a,b)=>a.label.localeCompare(b.label)||a.staffUserId-b.staffUserId);
  const teamRoster=<SectionShell eyebrow="My team" title={view.manager?.workload==null?'My team':`${teamRows.length} agents in workload`}
    sub="Current assignment · organization scope" testId="workspace-team-roster">
    <div className="cc-team-note"><strong>Manager</strong><span>Reviews permitted team work{canReassign?' and reassigns work':''}.</span></div>
    {view.manager?.workload==null?<EmptyState title="Workload view unavailable" body="Your assignment authority does not include this read."/>:
      <DataTable label="Agent roster" testId="workspace-team-table" columns={[
        {key:'agent',header:'Agent',render:row=><><strong>{row.label}</strong><small>{row.paused?'Paused - keeps existing work':'Active'}</small></>},
        {key:'assigned',header:'Assigned open leads',align:'end',render:row=><><span>{row.openCount.toLocaleString('en-IN')}</span><small>{row.overdueCount.toLocaleString('en-IN')} overdue</small></>},
        ...['New','Callback','Contacted','Needs review'].map(label=>({key:label,header:label,align:'end' as const,render:()=> <span aria-label={`${label} count unavailable`}>—</span>})),
        {key:'action',header:'Action',render:row=><AcButton className="cc-quiet" variant="ghost" data-testid={`workspace-team-open-${row.staffUserId}`}
          ref={element=>{if(element&&teamReturnAgent.current===row.staffUserId){element.focus({preventScroll:true});teamReturnAgent.current=null;}}}
          onKeyDown={event=>event.stopPropagation()} onClick={event=>{event.stopPropagation();selectWorkloadAgent(row.staffUserId);}}>Open agent queue →</AcButton>},
      ]} rows={teamRows} rowKey={row=>row.staffUserId} rowTestId={row=>`workspace-team-agent-${row.staffUserId}`}
        onRowClick={row=>selectWorkloadAgent(row.staffUserId)} empty={<EmptyState title="No assigned work yet" body="No agent currently owns open leads."/>}/>}
    <p className="cc-team-foot">Stage breakdown unavailable. Agent queues show permitted current work; their counts can differ from organization assignments.</p>
  </SectionShell>;

  const workspaceHeader = (
      <PageHeader
        eyebrow={monthlyManager ? "Call center manager" : "Call center"}
        title={isManager ? (myTeam ? 'My team' : searchParams.get('view')==='leads'?'Leads':'Team operations') : 'My Work'}
        /* PHONE-IN-LEAD-CAPTURE-V1: the ONE page-level primary action
           (shell law), agent lens only. The lens is SERVER-decided
           (call_center_agent exactly), and that role is in the FROZEN
           write audience of POST /api/leads/enquiries -- the door never
           renders for a caller the write route would refuse
           (SEE-NOT-DO / LES-016). */
        actions={
          phoneInDoorVisible(view.lens) ? (
            <AcButton
              variant="primary"
              onClick={() => setPhoneInOpen(true)}
              data-testid="workspace-phone-in-door"
            >
              {PHONE_IN_DOOR_LABEL}
            </AcButton>
          ) : undefined
        }
        subs={monthlyManager ? [] : [
          <span className="workspace-freshness" key="freshness">
            {`As of ${formatIstDateTime(view.generatedAtIso)} IST`}
            <button
              type="button"
              className="ac-btn ac-btn--ghost ac-btn--sm workspace-freshness__refresh"
              onClick={() => setReloadKey((k) => k + 1)}
              data-testid="workspace-refresh"
            >
              Refresh
            </button>
          </span>,
        ]}
        testId="workspace-header"
      />
  );

  const dockedLead = isManager && !myTeam;
  function closeLead(): void {
    if (panelBusy) return;
    setOpenQuickView(null);
    if(monthlyManager){const next=new URLSearchParams(searchParams);next.delete('queueLead');setSearchParams(next,{replace:true});}
    requestAnimationFrame(() => {
      const opener=leadOpenerRef.current?.isConnected?leadOpenerRef.current:document.querySelector<HTMLButtonElement>(`[data-testid="workspace-${monthlyManager||leadOpenerRef.current?.classList.contains('cc-lead-name')?'name':'open'}-${openQuickView?.leadId}"]`) ?? document.querySelector<HTMLInputElement>('[aria-label="Search queue"]');
      opener?.focus({ preventScroll: true });
    });
  }
  const previewState = managerPreview?.leadId !== openQuickView?.leadId || managerPreview?.loading ?
    <LoadingState label="Loading permitted enquiry" /> :
    <EmptyState title="Enquiry unavailable" body="This enquiry could not be uniquely read in your current permitted queue. Close and refresh to try again." />;
  const selectedEnquiry = view.items.find(item=>item.leadId===managerPreview?.leadId);
  const managerCard = managerPreview?.model ? (
<Detail label={`${managerPreview.model.title} context`} testId="lead-quickview" frame="panel" head={<>
        <div className="cc-enquiry-heading"><span className="ac-text-caption">Enquiry #{managerPreview.leadId}</span>{dockedLead&&<AcButton className="cc-quiet" variant="ghost" ref={panelCloseRef} size="sm" onClick={closeLead} aria-label="Close lead details">Close</AcButton>}</div>
        <h2 className="ac-detail__title" data-testid="quickview-title">{managerPreview.model.title}</h2>
        <span data-testid="quickview-owner-static">{managerPreview.model.ownership?.ownerLabel??'Assigned agent unavailable'}</span>
      </>} foot={<span data-testid="quickview-readonly">Read-only · Contact, phone, clinical, payment and full history details unavailable.</span>}>
        <nav className="cc-enquiry-tabs" aria-label="Enquiry sections">
          <AcButton aria-pressed={enquiryTab==='overview'} onClick={()=>setEnquiryTab('overview')}>Overview</AcButton>
          <AcButton aria-pressed={enquiryTab==='activity'} onClick={()=>setEnquiryTab('activity')}>Activity {managerPreview.model.activity.length}</AcButton>
        </nav>
        {enquiryTab==='overview'?<>
          <WorkRegion eyebrow="Next action" title={managerPreview.model.nextAction??'Next action unavailable'}>{null}</WorkRegion>
          <div className="cc-enquiry-reference"><h3>Reference</h3><dl>
            {[['Lead ID',String(managerPreview.leadId)],['Source',managerPreview.model.sourceLabel],['Created','—'],['Owner',managerPreview.model.ownership?.ownerLabel],['Phone','—'],['Clinic',managerPreview.model.clinicLabel]].map(([label,value])=><div key={label}><dt>{label}</dt><dd>{value??'—'}{label==='Owner'&&canReassign&&selectedEnquiry?.leadId&&<AcButton className="cc-quiet" variant="ghost" data-testid={`workspace-reassign-${selectedEnquiry.leadId}`} onClick={()=>setReassign({leadId:selectedEnquiry.leadId as number,displayName:rowDisplayName(selectedEnquiry),currentOwnerLabel:selectedEnquiry.owner.label,currentOwnerStaffId:selectedEnquiry.owner.staffId})}>Reassign</AcButton>}</dd></div>)}
          </dl>
          <details className="cc-enquiry-details"><summary>Work details</summary><AcButton variant="ghost" className="cc-quiet" data-testid="workspace-refresh" onClick={()=>setReloadKey(k=>k+1)}>Refresh</AcButton><dl>
            <div><dt>Status</dt><dd>{managerPreview.model.stageLabel}</dd></div><div><dt>Waiting</dt><dd>{managerPreview.model.waitingLabel}</dd></div>
            <div><dt>Reason</dt><dd>{selectedEnquiry?.workReasonLabel??'Unavailable'}</dd></div><div><dt>Due</dt><dd data-testid="workspace-row-due">{selectedEnquiry?dueStateLabel(selectedEnquiry):'Unavailable'}</dd></div>
          </dl>{selectedEnquiry?.lastActivity&&<p>{selectedEnquiry.lastActivity.label} · {selectedEnquiry.lastActivity.atIso?formatIstDateTime(selectedEnquiry.lastActivity.atIso)+' IST':'Time unavailable'}</p>}<p>Creation time is not supplied by this permitted projection.</p></details>
          </div>
        </>:<div className="cc-enquiry-reference" data-testid="quickview-activity"><h3>Latest permitted activity</h3>{managerPreview.model.activity.length? <ol>{managerPreview.model.activity.map((entry,index)=><li key={index}>{entry.label}{entry.whenLabel&&<p>{entry.whenLabel}</p>}</li>)}</ol>:<p>No recorded activity yet.</p>}</div>}
      </Detail>
  ) : null;
  const quickView = openQuickView !== null ? monthlyManager ? (
    managerPreview?.leadId !== openQuickView.leadId || managerPreview.loading || managerPreview.model === null ?
      dockedLead ? previewState : <DrawerShell title="Enquiry" onClose={closeLead}>{previewState}</DrawerShell> :
      dockedLead ? managerCard : <DrawerShell title="Enquiry" onClose={closeLead}>{managerCard}</DrawerShell>
  ) : (
        <LeadQuickViewHost
          key={openQuickView.leadId}
          frame={dockedLead ? "panel" : "drawer"}
          onBusyChange={dockedLead ? setPanelBusy : undefined}
          leadId={openQuickView.leadId}
          seedTitle={openQuickView.title}
          onClose={closeLead}
          onChanged={() => {
            // The lead has been worked: close back to the queue and refetch,
            // so the re-sorted queue's top row becomes the current row again
            // (continuous one-queue work -- without a modal on load).
            setOpenQuickView(null);
            if(monthlyManager){const next=new URLSearchParams(searchParams);next.delete('queueLead');setSearchParams(next,{replace:true});}
            setSelectedKey(null);
            setReloadKey((k) => k + 1);
          }}
        />
  ) : null;

  return (
    <section
      className={isManager ? "callcenter-workspace cc-monthly-workspace" : "callcenter-workspace"}
      data-testid={isManager ? 'workspace-manager' : 'workspace-agent'}
      aria-label={isManager ? 'Team operations' : 'My Work'}
      onKeyDown={event => {
        // The shared table opens on keydown. Opening moves focus to Close;
        // consume Enter's native activation so it cannot click that new target.
        if (monthlyManager && event.key === 'Enter' && event.target instanceof HTMLTableRowElement) {
          event.preventDefault();
        }
        if (event.key === 'Escape' && !event.defaultPrevented && dockedLead && openQuickView && !reassign && !panelBusy) {
          event.preventDefault();
          closeLead();
        }
      }}
    >
      {!isManager && workspaceHeader}
      {isManager && <PeriodReports key="manager-report" summary heading={workspaceHeader} workspaceRefresh={monthlyManager?pageRefresh:undefined} />}



      {!isManager && queueCounters}

      {monthlyManager&&myTeam?teamRoster:<div className={isManager&&!myTeam?'cc-manager-workplane':''}>


      {/* OVERNIGHT HOLD (CALL-CENTER-TENANT-ROUTING-RULES-V1): the manager
          release action + the last-run summary. Rendered ONLY when the
          overnight-hold seam is active (the /me release signal + the count
          fragment both present) -- absent when the rule is off (FAIL-SAME). */}
      {isManager &&
      me?.overnightReleaseEnabled === true &&
      view.manager?.overnight !== undefined ? (
        <SectionShell
          eyebrow="Team"
          title="Overnight leads"
          sub="Leads captured outside working hours are held unassigned until you release them."
          testId="workspace-overnight"
        >
          <p className="ac-text-body" data-testid="workspace-overnight-count">
            <strong>{view.manager.overnight.awaitingRelease}</strong> overnight
            lead{view.manager.overnight.awaitingRelease === 1 ? '' : 's'} awaiting
            release ({view.manager.overnight.scopeLabel}).
          </p>
          <div className="workspace-overnight-actions">
            <AcButton
              variant="primary"
              size="sm"
              data-testid="workspace-overnight-release"
              disabled={view.manager.overnight.awaitingRelease === 0}
              onClick={() => setReleaseOpen(true)}
            >
              Release overnight leads
            </AcButton>
          </div>
          {releaseResult !== null ? (
            <div
              className="workspace-overnight-summary"
              role="status"
              data-testid="workspace-overnight-summary"
            >
              <p className="ac-text-body">
                Released <strong>{releaseResult.releasedCount}</strong> of{' '}
                {releaseResult.heldCount} ({releaseResult.continuityCount} to a
                prior agent, {releaseResult.dealtCount} dealt across the team
                {releaseResult.skippedCount > 0
                  ? `, ${releaseResult.skippedCount} skipped`
                  : ''}
                ).
              </p>
              {releaseResult.perAgent.length > 0 ? (
                <ul className="workspace-overnight-tally">
                  {releaseResult.perAgent.map((a) => (
                    <li key={a.staffUserId}>
                      {a.label}: {a.count}
                      {a.viaContinuity > 0
                        ? ` (${a.viaContinuity} returning)`
                        : ''}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </SectionShell>
      ) : null}

      {/* QUEUE-HEADING-V1 (Lane 5, 2026-09-10): the queue now carries a
          VISIBLE name and an honest count, in the SAME SectionShell primitive
          the workload block above already uses. Before this, DataTable's
          `label` reached only `aria-label`, so the eye met an unnamed,
          uncounted table under a row of chips -- reproduced at 1440 and 834 in
          this package's frames. No new primitive is minted and no CSS file is
          touched (dashboard.css belongs to another lane). */}
      <SectionShell
        eyebrow={monthlyManager?undefined:"Queue"}
        title={monthlyManager ? filters.agentStaffId===null?'Team leads':`${agentLabelById.get(filters.agentStaffId)??'Selected agent'}’s leads` : isManager?'Team queue':'My queue'}
        sub={monthlyManager?undefined:queueCountLine(visibleItems.length, view.items.length)}
        testId="workspace-queue-section"
      >
      {isManager && <div className="cc-queue-toolbar">
        <label className="cc-queue-search">
          <span className={monthlyManager?"cc-sr-only":undefined}>Search queue</span>
          <input aria-label="Search queue" type="search" disabled={panelBusy} value={queueSearch}
            onChange={event => setQueueSearch(event.target.value)}
            placeholder="Search lead ID, name or agent" />
        </label>
        {[
          { key: 'source', label: 'Queue source', value: queueSource,
            options: [{ value: '', label: 'All sources' }, ...sourceOptions],
            change: (value: string) => setQueueScope('queueSource', value) },
          { key: 'agent', label: 'Queue agent', value: filters.agentStaffId === null ? '' : String(filters.agentStaffId),
            options: [{ value: '', label: 'All agents' }, ...Array.from(agentLabelById, ([id, label]) => ({ value: String(id), label }))],
            change: (value: string) => writeFilters({ ...filters, agentStaffId: value ? Number(value) : null }) },
          { key: 'state', label: 'Queue state', value: queueState,
            options: [{ value: '', label: 'All states' }, ...stateOptions],
            change: (value: string) => setQueueScope('queueState', value) },
        ].map(control => <label key={control.key} className="cc-queue-select">
          <span className={monthlyManager?"cc-sr-only":undefined}>{control.label}</span>
          <select className="ac-select" aria-label={control.label} disabled={panelBusy} value={control.value} onChange={event => control.change(event.target.value)}>
            {control.value && !control.options.some(option => option.value === control.value)
              ? <option value={control.value}>Unavailable selection</option> : null}
            {control.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>)}
      </div>}

      {recoveryUnavailable && (!monthlyManager||view.items.length>0) && <p className="panel-caveat" role="status" data-testid="workspace-recovery-unavailable">{monthlyManager?<>Permitted work only · missed-appointment recovery excluded.</>:<><strong>Missed-appointment information unavailable.</strong> Your role does not have access to missed-appointment recovery. Queue counts and filters cover permitted work only; they exclude missed appointments.</>}</p>}
      {isManager && !monthlyManager && queueCounters}
      {monthlyManager ? <>
        {view.items.length>0&&<div className="workspace-filters" role="group" aria-label="Queue status" data-testid="workspace-filters">
          {['',...businessStatuses].map(status=><AcButton key={status} size="sm" variant={businessStatus===status?'primary':'secondary'} aria-pressed={businessStatus===status} onClick={()=>setBusinessStatus(status)}>{status||'All'} {(status?statusItems.filter(item=>managerBusinessStatus(item)===status):statusItems).length.toLocaleString('en-IN')}</AcButton>)}
        </div>}
        <div className="cc-queue-context"><span>{filters.due==='breach'?'Overdue follow-ups':''}</span>{(businessStatus||queueSearch||queueSource||queueState||filters.agentStaffId!==null||filters.due!=='all'||filters.reasons.length>0)&&<AcButton className="cc-quiet" variant="ghost" onClick={()=>writeFilters({reasons:[],due:'all',agentStaffId:null},true)}>Clear filters</AcButton>}</div>
      </> : <div
        className="workspace-filters"
        role="group"
        aria-label="Queue filters"
        data-testid="workspace-filters"
      >
        {/* KEPT DELIBERATELY (COMMAND-CENTER-CRAFT-V2). Dropping this label
            would have shaved one element off the census, and it is exactly
            the trade the founder's amendment forbids: "do not delete a
            control someone uses to do their job in order to win a count".
            ENTERPRISE-EXPERIENCE-V2 shipped it as the five-second scan
            anchor for this group and pinned it; aria-label serves screen
            readers, not the eye it was written for. The count stays higher. */}
        {!monthlyManager&&<span className="workspace-filters__label" aria-hidden="true">Filters</span>}
        {monthlyManager&&queueCounters}
        {/* COMMAND-CENTER-CRAFT-V1: chips whose filter already has a counted
            tile above are NOT rendered -- the tile is the same control with a
            count and a tone on it. Nothing becomes unreachable; the duplicate
            stops taking space. */}
        {view.availableReasons
          .filter((reason) => !reasonHasCountedTile(reason, isManager))
          .map((reason) => {
          // CORRECTIVE-747 ROUND 2 (Gap 1a): the label is authored for the
          // WHOLE set this chip counts, never sampled from one arbitrary
          // member via .find() -- a set containing 1 due callback and 3
          // promised (upcoming) callbacks must not wear the label of the
          // one member .find() happened to return ("Callback due 4" when
          // three of the four are not due). WORKSPACE_REASON_FALLBACK_LABELS
          // is now the ONE label source for every reason chip, not merely
          // its empty-set fallback.
          const label = WORKSPACE_REASON_FALLBACK_LABELS[reason];
          const active = filters.reasons.includes(reason);
          // COMMAND-CENTER-CRAFT-V2: the surviving chips were the only
          // controls on this screen with no count anywhere. They filter the
          // LOADED items client-side, so a count of those items is exactly
          // what the chip does -- not an estimate.
          const count = view.items.filter((i) => i.workReason === reason).length;
          return (
            <button
              key={reason}
              type="button"
              className={`ac-btn ac-btn--${active ? 'primary' : 'ghost'} ac-btn--sm`}
              aria-pressed={active}
              data-testid={`workspace-filter-${reason}`}
              onClick={() => writeFilters(toggleReason(filters, reason))}
            >
              {`${label} ${count}`}
            </button>
          );
        })}
        <span className="workspace-filters__divider" aria-hidden="true" />
        {WORKSPACE_DUE_FILTERS.filter((due) => !dueHasCountedTile(due)).map((due) => (
          <button
            key={due}
            type="button"
            className={`ac-btn ac-btn--${filters.due === due ? 'primary' : 'ghost'} ac-btn--sm`}
            aria-pressed={filters.due === due}
            data-testid={`workspace-due-${due}`}
            onClick={() => writeFilters({ ...filters, due })}
          >
            {WORKSPACE_DUE_FILTER_LABELS[due]}
          </button>
        ))}
        {filters.agentStaffId !== null ? (
          <AcBadge tone="info" testId="workspace-agent-filter">
            {`Agent: ${agentLabelById.get(filters.agentStaffId) ?? `Staff #${filters.agentStaffId}`}`}
          </AcBadge>
        ) : null}
      </div>}
      <DataTable
        label={isManager ? 'Team queue' : 'My queue'}
        testId="workspace-queue"
        columns={columns}
        rows={pagedItems}
        rowKey={rowKeyOf}
        // The CURRENT ROW is marked, never covered. Grammar reused verbatim
        // from the banked `lead-list__row--selected` idiom (brand wash + a
        // 3px inset strip -- a strip AND a wash, never colour alone).
        rowClassName={(item) =>
          rowKeyOf(item) === currentRowKey
            ? 'callcenter-workspace__row--current'
            : undefined
        }
        rowTestId={(item) =>
          rowKeyOf(item) === currentRowKey ? 'workspace-row-current' : undefined
        }
        // The operator's intent door. DataTable makes the row focusable and
        // activates it on Enter, so this is the click AND the keyboard path.
        onRowClick={monthlyManager?item=>openRow(item):undefined}
        empty={
          view.items.length === 0 && monthlyManager ? (
            <div className="ac-table-scroll cc-filtered-empty-table"><table className="table ac-table" aria-label="Team queue"><colgroup>{columns.map(column=><col key={column.key} style={{width:column.width}}/>)}</colgroup><thead><tr>{columns.map(column=><th key={column.key}>{column.header}</th>)}</tr></thead><tbody><tr><td colSpan={columns.length}><EmptyState title="No leads to show" testId="workspace-empty" body={`Only work your role can access is shown.${recoveryUnavailable?' Missed-appointment recovery is not included.':''}`}/></td></tr></tbody></table></div>
          ) : view.items.length === 0 ? (
            <EmptyState
              title={recoveryUnavailable ? 'No permitted queue work to show' : isManager ? 'The team is all caught up' : 'You are all caught up'}
              body={monthlyManager?`Only work your role can access is shown.${recoveryUnavailable?' Missed-appointment recovery is not included.':''}`:recoveryUnavailable?'Missed-appointment information is unavailable, so this is not a complete view of team work.':'Nothing needs work right now. New work appears here the moment it arrives.'}
              testId="workspace-empty"
            />
          ) : monthlyManager ? (
            <div className="ac-table-scroll cc-filtered-empty-table" data-testid="workspace-queue">
              <table className="table ac-table" aria-label="Team queue">
                <colgroup>{columns.map(column=><col key={column.key} style={{width:column.width}}/>)}</colgroup>
                <thead><tr>{columns.map(column=><th key={column.key}>{column.header}</th>)}</tr></thead>
                <tbody><tr><td colSpan={columns.length}>
                  <EmptyState title={filters.due==='breach'?"No overdue leads":"No leads match these filters"} body={filters.due==='breach'?"No overdue leads in this permitted queue.":"Try another name, source or agent."}
                    testId="workspace-empty-filtered"
                    action={<AcButton onClick={()=>writeFilters({reasons:[],due:'all',agentStaffId:null},true)}>Clear filters</AcButton>}/>
                </td></tr></tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              title={monthlyManager?"No leads match these filters":"Nothing matches these filters"}
              body={monthlyManager?"Try a different lead ID, source or agent.":"Clear a filter to see the rest of the queue."}
              action={
                <AcButton
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    writeFilters({ reasons: [], due: 'all', agentStaffId: null }, true)
                  }
                >
                  Clear filters
                </AcButton>
              }
              testId="workspace-empty-filtered"
            />
          )
        }
      />

      {isManager && (!monthlyManager||visibleItems.length>0) && <div className="cc-report-controls" ref={loadMoreRef} data-more={pagedItems.length<visibleItems.length} data-busy={panelBusy||openQuickView!==null}>
        <span aria-live="polite">{pagedItems.length.toLocaleString('en-IN')} of {visibleItems.length.toLocaleString('en-IN')} leads shown</span>
        {pagedItems.length<visibleItems.length && <AcButton disabled={panelBusy} onClick={()=>setQueuePage(page=>page+1)}>Load more</AcButton>}
      </div>}

      </SectionShell>
      {dockedLead && openQuickView !== null ? <aside className="cc-workload-rail cc-lead-panel" aria-label="Selected lead" data-testid="workspace-lead-panel">
        {(!monthlyManager||!managerPreview?.model)&&<div className="cc-lead-panel__controls"><AcButton ref={panelCloseRef} size="sm" disabled={panelBusy} onClick={closeLead} aria-label="Close lead details">Close</AcButton></div>}
        {quickView}
      </aside> : isManager && view.manager !== undefined && <aside className={monthlyManager&&!myTeam?"cc-workload-rail cc-attention-rail":"cc-workload-rail"}>
        <SectionShell eyebrow={monthlyManager?undefined:"Team"} title={myTeam?'My team':monthlyManager?'Needs attention':'Workload by agent'} sub={monthlyManager&&!myTeam?"Current permitted workload":monthlyManager?`${workload.length} agents in workload · organization scope`:"Whole organization assigned load; independent of the queue scope and report period."} testId="workspace-workload">
      {isManager && !monthlyManager && openingLine.clauses.length > 0 ? (
        <OpeningLine
          state={openingLine.state}
          clauses={openingLine.clauses}
          testId="workspace-opening-line"
        />
      ) : null}

          {monthlyManager&&view.items.length===0?<p className="cc-state-rail">No permitted open leads.</p>:view.manager.workload===null?<EmptyState title="Workload view unavailable" body="Your assignment authority does not include this read."/>:<DataTable label="Workload by agent" testId="workspace-workload-table" columns={[
            {key:'agent',header:<AcButton size="sm" variant="ghost" aria-pressed={workSort==='agent'} aria-description={workSort==='agent'?(workDescending?'Sorted descending':'Sorted ascending'):'Not sorted'} onClick={()=>{setWorkDescending(workSort==='agent'?!workDescending:false);setWorkSort('agent')}}>Agent{(!monthlyManager||myTeam)&&workSort==='agent'?(workDescending?' ↓':' ↑'):''}</AcButton>,render:row=><>{monthlyManager&&!myTeam?<AcButton className="cc-quiet cc-attention-agent" variant="ghost" onKeyDown={event=>event.stopPropagation()} onClick={event=>{event.stopPropagation();selectWorkloadAgent(row.staffUserId);}}>{row.label}</AcButton>:<span>{row.label}</span>}{(!monthlyManager||row.paused)&&<small className="cc-agent-state">{row.paused?'Paused - keeps existing work':'Active'}</small>}</>},
            ...(monthlyManager&&!myTeam ? [
              {key:'overdue',header:<AcButton size="sm" variant="ghost" aria-label="Sort by overdue" aria-pressed={workSort==='overdue'} aria-description={workSort==='overdue'?(workDescending?'Sorted descending':'Sorted ascending'):'Not sorted'} onClick={()=>{setWorkDescending(workSort==='overdue'?!workDescending:true);setWorkSort('overdue');}}>Overdue</AcButton>,render:(row:typeof workload[number])=><AcButton className="cc-quiet" variant="ghost" aria-label={`${row.label}: ${row.overdueCount} overdue`} onKeyDown={event=>event.stopPropagation()} onClick={event=>{event.stopPropagation();selectWorkloadAgent(row.staffUserId,true);}}>{row.overdueCount.toLocaleString('en-IN')}</AcButton>},
              {key:'today',header:'Due today',render:()=> <span aria-label="Due today unavailable">—</span>},
              {key:'open',header:<AcButton size="sm" variant="ghost" aria-label="Sort by open" aria-pressed={workSort==='open'} aria-description={workSort==='open'?(workDescending?'Sorted descending':'Sorted ascending'):'Not sorted'} onClick={()=>{setWorkDescending(workSort==='open'?!workDescending:true);setWorkSort('open');}}>Open</AcButton>,render:(row:typeof workload[number])=><AcButton className="cc-quiet" variant="ghost" aria-label={`${row.label}: ${row.openCount} open`} onKeyDown={event=>event.stopPropagation()} onClick={event=>{event.stopPropagation();selectWorkloadAgent(row.staffUserId);}}>{row.openCount.toLocaleString('en-IN')}</AcButton>},
            ] : [
            {key:'open',header:<AcButton size="sm" variant="ghost" aria-pressed={workSort==='open'} onClick={()=>{setWorkDescending(workSort==='open'?!workDescending:true);setWorkSort('open')}} aria-label="Sort by open">Open total{workSort==='open'?(workDescending?' ↓':' ↑'):''}</AcButton>,align:'end' as const,render:(row:typeof workload[number])=><><span>{row.openCount.toLocaleString('en-IN')}</span></>},
            {key:'overdue',header:<AcButton size="sm" variant="ghost" aria-pressed={workSort==='overdue'} onClick={()=>{setWorkDescending(workSort==='overdue'?!workDescending:true);setWorkSort('overdue')}} aria-label="Sort by overdue">Overdue{workSort==='overdue'?(workDescending?' ↓':' ↑'):''}</AcButton>,align:'end' as const,render:(row:typeof workload[number])=><><span>{row.overdueCount.toLocaleString('en-IN')}</span></>},
            ]),
            ...(!monthlyManager ? [{key:'drill',header:'Actions',render:renderWorkloadAction}] : []),

          ]} rows={workload} rowKey={row=>row.staffUserId} onRowClick={monthlyManager?row=>selectWorkloadAgent(row.staffUserId):undefined} rowClassName={row=>monthlyManager&&row.staffUserId===filters.agentStaffId?'callcenter-workspace__row--current':undefined} rowTestId={row=>`workspace-agent-${row.staffUserId}`}  empty={<EmptyState title="No assigned work yet" body="No agent currently owns open leads."/>}/>}
          {(!monthlyManager||view.items.length>0)&&<p className="ac-text-caption">{monthlyManager?'Due today unavailable':'Due today and detailed stage counts: unavailable from this workload read.'}</p>}
          {monthlyManager&&filters.agentStaffId!==null&&<AcButton className="cc-quiet" variant="ghost" onClick={allAgents}>All agents →</AcButton>}
          {view.manager.pausedWithNoOpenWork.length>0&&<p data-testid="workspace-paused-noload">Paused with no open work: {view.manager.pausedWithNoOpenWork.map(p=>p.label).join(', ')}</p>}
          {quickViewLawful&&<p>Open in Lead operations: <Link data-testid="workspace-leadops-unassigned" to={leadOperationsDrillPath('unassigned')}>Unassigned pool</Link></p>}
        </SectionShell>
      </aside>}
      </div>}

      {!dockedLead && quickView}

      {reassign !== null ? (
        <ReassignDialog
          target={reassign}
          onClose={() => setReassign(null)}
          onDone={() => {
            const next=new URLSearchParams(searchParams);next.set('queueLead',String(reassign.leadId));next.set('queuePage',String(currentQueuePage));setSearchParams(next,{replace:true});
            setSelectedKey(`lead-${reassign.leadId}`);
            setReassign(null);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {releaseOpen && view.manager?.overnight !== undefined ? (
        <ReleaseOvernightDialog
          count={view.manager.overnight.awaitingRelease}
          onClose={() => setReleaseOpen(false)}
          onDone={(result) => {
            setReleaseResult(result);
            setReleaseOpen(false);
            setReloadKey((k) => k + 1);
          }}
        />
      ) : null}

      {/* PHONE-IN-LEAD-CAPTURE-V1: the capture modal. On a close after at
          least one capture the queue refetches, so a phone-in that routed
          to this agent appears without a manual refresh. The refresh runs
          on CLOSE (never mid-success) so the open modal is never unmounted
          under the agent by the loading skeleton. */}
      {phoneInOpen ? (
        <PhoneInEnquiryModal
          onClose={(captured) => {
            setPhoneInOpen(false);
            if (captured) setReloadKey((k) => k + 1);
          }}
        />
      ) : null}
    </section>
  );
}

/** The ONE label source for every reason chip (CORRECTIVE-747 ROUND 2, Gap
 *  1a) -- a SET's label, never sampled from one of its members via .find().
 *  Originally named/used as the empty-set fallback only (when the loaded
 *  queue carried no row for a reason); it is now the label for that reason's
 *  chip UNCONDITIONALLY, loaded or not. Every entry stays neutral over the
 *  reason's possible urgency tiers -- 'callback' reads 'Callbacks', never
 *  due-specific wording, because a callback chip's count can mix due-now and
 *  promised (upcoming) rows. Mirrored verbatim in callcenter-workspace-view.ts
 *  WORKSPACE_REASON_LABELS_FALLBACK -- change BOTH maps together. */
export const WORKSPACE_REASON_FALLBACK_LABELS: Readonly<
  Record<WorkspaceWorkReason, string>
> = Object.freeze({
  new_lead: 'New lead',
  call_now: 'Call now',
  callback: 'Callbacks',
  appointment_confirmation: 'Confirm appointment',
  waiting_response: 'Waiting on response',
  no_show_recovery: 'Missed appointment',
  overdue_followup: 'Follow-up due',
  escalation: 'Needs attention',
  routing_exception: 'Unassigned - needs owner',
});
